import { useEffect, useMemo, useState, memo, useRef } from 'react';
import useLasertagGame from '../../hooks/useLasertagGame';
import useInternalBooking from '../../hooks/useInternalBooking';
import { toast } from 'react-toastify';
import {
  Box,
  Alert,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  ButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Stack,
  Collapse,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Switch,
  Slider,
  FormControlLabel,
  RadioGroup,
  Radio,
  Chip,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Close,
  CallMerge,
  AutoAwesome,
  VolumeUp,
  PlayCircle,
  StopCircle,
  VideoLibrary,
  SkipNext,
  Tv,
} from '@mui/icons-material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
  DndContext,
  pointerWithin,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core';
import {
  sortableKeyboardCoordinates,
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { NumberChip } from '../Bookings/components/BookingIcons';
import GroupNameDisplay from '../Bookings/components/GroupNameDisplay';
import dayjs from 'dayjs';
import { isHexWhiteLike } from '../../util/hexColor';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BookingDetailsModal from '../Bookings/BookingDetailsModal/BookingDetailsModal';
import useEntertainment from '../../hooks/useEntertainment';
import useBranch from '../../hooks/useBranch';
import useLocationStatus from '../../hooks/useLocationStatus';
import useAutoAnnouncements from '../../hooks/useAutoAnnouncements';
import {
  splitArrayToState,
  splitStateToArray,
  sanitizeSplitStateForAssignments,
  areSplitStatesEqual,
  normalizeSlotId,
  getSlotColorById,
  getSlotLabelById,
  isPlaceholderMember,
  getPlaceholdersCount,
  getEffectiveParticipantCount,
  getAdjustedEffectiveParticipantCount as getAdjustedEffectiveParticipantCountUtil,
  getAdjustedCountWithMap,
  getTimingInfoForBooking,
  getTimingInfoForBookingDetailed,
  getExpectedGamesForBooking,
  hasPlayedEnoughGames,
  isInsideBookingWindow,
  computeAutoAssignments,
  compactTeamAssignments,
  LASERTAG_TEAM_COLORS,
} from '../../util/operatorUtils';

const Operator = () => {
  const theme = useTheme();
  const {
    getOperatorContext,
    getParticipantsForPreparing,
    getLastAssignmentBySlot,
    startGame,
    endGame,
    updateGroupAssignment,
    deleteGroupAssignment,
    updateSplitQuantities,
    confirmPendingCheckin,
    declinePendingCheckin,
    acceptLateCheckin,
    updateSitOutForPreparing,
    updateInformedForPreparing,
    updateGroupApproval,
    getLasertagGamesForOperator,
  } = useLasertagGame();
  const { updateInternalBooking, markLTCustomerAsBriefed } = useInternalBooking();
  const { getAllEntertainmentsByBranch } = useEntertainment();
  const { getBranches } = useBranch();

  // States
  const [games, setGames] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('all'); // 'green' | 'orange' | 'all'
  const [participantsTick, setParticipantsTick] = useState(0);
  const [assignmentsTick, setAssignmentsTick] = useState(0);
  const [teamCount, setTeamCount] = useState(2);
  const [branchOptions, setBranchOptions] = useState([]);
  const [branch, setBranch] = useState('');
  const [entertainmentOptions, setEntertainmentOptions] = useState([]);
  const [entertainmentId, setEntertainmentId] = useState('');
  const [gaLoading, setGaLoading] = useState(false);
  const [pendingWebview, setPendingWebview] = useState(false);
  const pendingWebviewRef = useRef(false);
  const [gaBookings, setGaBookings] = useState([]);
  const [teamAssignmentFull, setTeamAssignmentFull] = useState(null);
  const [groupAssignments, setGroupAssignments] = useState([]);
  const [lastReuseAvailable, setLastReuseAvailable] = useState(false);
  const [lastReuseTemplate, setLastReuseTemplate] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [splitQuantities, setSplitQuantities] = useState({});
  const [sitOutCounts, setSitOutCounts] = useState({});
  const [activeSitOutCounts, setActiveSitOutCounts] = useState({});
  const [splitDialog, setSplitDialog] = useState({
    open: false,
    booking: null,
    teamA: 1,
    teamB: 2,
  });
  const [clearConfirmDialog, setClearConfirmDialog] = useState({ open: false });
  const [endConfirmDialog, setEndConfirmDialog] = useState({ open: false, game: null });
  const [isEndingGame, setIsEndingGame] = useState(false);
  const [isStartingGame, setIsStartingGame] = useState(false);
  const [lateCheckinDialog, setLateCheckinDialog] = useState({ open: false, list: [] });
  const [startConfirmDialog, setStartConfirmDialog] = useState({
    open: false,
    gameId: null,
    details: [],
    deltasByBooking: {},
  });
  const [detailsModal, setDetailsModal] = useState({
    open: false,
    bookingId: null,
    borderColor: null,
  });

  const { audioStatus, briefingStatus, lfStatus, sendCommand } = useLocationStatus(entertainmentId);
  const {
    config: autoAnnouncementsConfig,
    updateConfig: updateAutoAnnouncements,
    skipAnnouncement: skipAutoAnnouncement,
  } = useAutoAnnouncements(entertainmentId);
  const [audioControlsExpanded, setAudioControlsExpanded] = useState(false);
  const [briefingCountdown, setBriefingCountdown] = useState(null);
  const [gameStartCountdown, setGameStartCountdown] = useState(null);
  const [lastEndedGame, setLastEndedGame] = useState(null);
  const [countUpTick, setCountUpTick] = useState(0);
  const [activeUnassigned, setActiveUnassigned] = useState({
    unassignedBookings: [],
    unassignedMembers: [],
  });
  const [briefingSliderValue, setBriefingSliderValue] = useState(null);
  const [gameStartSliderValue, setGameStartSliderValue] = useState(null);

  // Refs
  const dragEpochRef = useRef(0);
  const participantsIntervalRef = useRef(null);
  const assignmentsIntervalRef = useRef(null);
  const hasInitializedTeamCountRef = useRef(false);
  const splitQuantitiesRef = useRef(splitQuantities);
  const didModifyDetailsRef = useRef(false);
  const previousActiveGameIdRef = useRef(null);

  // Functions
  const getSlotParamForRequests = () =>
    selectedSlot === 'green' ? 1 : selectedSlot === 'orange' ? 0 : 2;

  const startParticipantsTimer = () => {
    if (participantsIntervalRef.current) return;
    participantsIntervalRef.current = setInterval(() => {
      setParticipantsTick(t => t + 1);
    }, 5000);
  };

  const startAssignmentsTimer = () => {
    if (assignmentsIntervalRef.current) return;
    assignmentsIntervalRef.current = setInterval(() => {
      setAssignmentsTick(t => t + 1);
    }, 30000);
  };

  const stopParticipantsTimer = () => {
    if (participantsIntervalRef.current) {
      clearInterval(participantsIntervalRef.current);
      participantsIntervalRef.current = null;
    }
  };

  const stopAssignmentsTimer = () => {
    if (assignmentsIntervalRef.current) {
      clearInterval(assignmentsIntervalRef.current);
      assignmentsIntervalRef.current = null;
    }
  };

  const getAdjustedEffectiveParticipantCount = booking => {
    return getAdjustedEffectiveParticipantCountUtil(booking, sitOutCounts);
  };

  const persistSplitQuantities = async (nextState, previousState, overrideTeamCount = null) => {
    if (!entertainmentId) {
      return;
    }
    try {
      await updateSplitQuantities({
        splitQuantities: nextState,
        teamCount: overrideTeamCount != null ? overrideTeamCount : teamCount,
        slot: getSlotParamForRequests(),
        entertainmentId,
      });
    } catch (err) {
      toast.error('Fehler beim Speichern der Aufteilung');
      setSplitQuantities(previousState);
      throw err;
    }
  };

  // Lock game control while Laserforce is actively controlling the game via LFstatus topic
  const isLfLocked = useMemo(() => {
    try {
      if (!lfStatus || !lfStatus.status) return false;
      const status = String(lfStatus.status).toLowerCase();
      return status !== 'offline';
    } catch (_) {
      return false;
    }
  }, [lfStatus?.status]);

  const canControlGames = !isLfLocked;
  const handleOpenDetails = booking => {
    // Opens the booking details modal for the provided booking
    if (!booking || !booking._id) return;
    didModifyDetailsRef.current = false;
    const slotId = normalizeSlotId(booking?.ltSpecific?.playerGroupID);
    const color = getSlotColorById(slotId, theme);
    setDetailsModal({ open: true, bookingId: String(booking._id), borderColor: color });
  };

  const handleDetailsUpdate = (bookingId, updated) => {
    try {
      didModifyDetailsRef.current = true;
      if (!bookingId) return;
      setGaBookings(prev => {
        const idStr = String(bookingId);
        const exists = prev.some(b => String(b._id) === idStr);
        if (updated && updated.status === 'removed') {
          return prev.filter(b => String(b._id) !== idStr);
        }
        if (!updated) return prev;
        if (!exists) return prev;
        return prev.map(b => (String(b._id) === idStr ? { ...b, ...updated } : b));
      });
      // If booking was removed, also clear any team assignments and split quantities locally
      if (updated && updated.status === 'removed') {
        setGroupAssignments(prev => prev.filter(a => String(a.bookingId) !== String(bookingId)));
        const previousSplits = splitQuantitiesRef.current || {};
        if (previousSplits[String(bookingId)]) {
          const nextSplits = { ...previousSplits };
          delete nextSplits[String(bookingId)];
          setSplitQuantities(nextSplits);
          persistSplitQuantities(nextSplits, previousSplits).catch(() => {});
        }
      }
    } catch (_) {}
  };

  const handleBranchChange = event => {
    const value = event.target.value;
    setBranch(value);
    try {
      sessionStorage.setItem('branch', value);
    } catch (_) {}
  };

  const handleEntertainmentChange = event => {
    const value = event.target.value;
    setEntertainmentId(value);
  };

  const handleCloseDetails = () => {
    const modified = !!didModifyDetailsRef.current;
    setDetailsModal({ open: false, bookingId: null, borderColor: null });
    if (modified) {
      setAssignmentsTick(t => t + 1);
      setParticipantsTick(t => t + 1);
    }
    didModifyDetailsRef.current = false;
  };

  // Effects
  useEffect(() => {
    splitQuantitiesRef.current = splitQuantities;
  }, [splitQuantities]);

  useEffect(() => {
    if (pendingWebviewRef.current) {
      pendingWebviewRef.current = false;
      setPendingWebview(false);
    }
  }, [briefingStatus.status]);

  useEffect(() => {
    try {
      if (!teamAssignmentFull) return;
      const raw = Array.isArray(teamAssignmentFull.splitQuantities)
        ? teamAssignmentFull.splitQuantities
        : [];
      const computed = splitArrayToState(raw);
      if (!areSplitStatesEqual(computed, splitQuantitiesRef.current || {})) {
        setSplitQuantities(computed);
      }
    } catch (_) {}
  }, [teamAssignmentFull]);

  // Load branches on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const branches = await getBranches();
        if (cancelled) return;
        const options = (branches || []).map(b => ({ id: b._id, label: b.locationName }));
        setBranchOptions(options);
        let initialBranch = '';
        try {
          const storedBranch = sessionStorage.getItem('branch');
          if (storedBranch && options.some(o => o.id === storedBranch)) {
            initialBranch = storedBranch;
          } else if (options.length > 0) {
            initialBranch = options[0].id;
          }
        } catch (_) {
          if (options.length > 0) {
            initialBranch = options[0].id;
          }
        }
        setBranch(initialBranch || '');
      } catch (err) {
        try {
          toast.error('Fehler beim Laden der Standorte');
        } catch (_) {}
        setBranchOptions([]);
        setBranch('');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load entertainments when branch changes
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!branch) {
        setEntertainmentOptions([]);
        setEntertainmentId('');
        return;
      }
      try {
        const list = await getAllEntertainmentsByBranch(branch);
        if (cancelled) return;
        const filtered = (list || []).filter(ent => {
          if (!ent || typeof ent.entertainmentName !== 'string') {
            return false;
          }
          return ent.entertainmentName.trim().toLowerCase() === 'laserzone';
        });
        const options = filtered.map(e => ({ id: e._id, label: e.entertainmentName }));
        setEntertainmentOptions(options);
        let initialEntertainment = '';
        try {
          const storedEntertainment = sessionStorage.getItem('entertainment');
          if (storedEntertainment && options.some(o => o.id === storedEntertainment)) {
            initialEntertainment = storedEntertainment;
          } else if (options.length > 0) {
            initialEntertainment = options[0].id;
          }
        } catch (_) {
          if (options.length > 0) {
            initialEntertainment = options[0].id;
          }
        }
        setEntertainmentId(initialEntertainment || '');
      } catch (err) {
        try {
          toast.error('Fehler beim Laden der Aktivitäten');
        } catch (_) {}
        setEntertainmentOptions([]);
        setEntertainmentId('');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [branch]);

  const hideBranchEntertainmentSelectors =
    branchOptions.length === 1 &&
    Boolean(branch) &&
    entertainmentOptions.length === 1 &&
    Boolean(entertainmentId);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        // Long enough that scroll gestures complete before drag starts (works with pan-y on draggable nodes)
        delay: 220,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Poll preparing participants (5s)
  useEffect(() => {
    let isMounted = true;
    if (activeId) {
      return () => {};
    }
    if (!entertainmentId) {
      return () => {};
    }
    const epochAtStart = dragEpochRef.current;
    const load = async () => {
      try {
        const slotParam = getSlotParamForRequests();
        const res = await getParticipantsForPreparing({ entertainmentId, slot: slotParam });
        if (!isMounted) return;
        if (dragEpochRef.current !== epochAtStart || activeId) {
          return;
        }
        const {
          participatingMembers = [],
          pendingCheckins = [],
          lateCheckins = [],
          gameId = null,
          slot = null,
          slotId = null,
          sitOutByBooking = {},
          informedByBooking = {},
        } = res || {};
        // Merge into games state (keep potential active game untouched)
        setGames(prev => {
          const next = [...prev];
          const preparingIndex = next.findIndex(g => g.status === 'Preparing');
          const preparingSlot = slotId != null ? slotId : slot;
          const preparing = gameId
            ? {
                _id: gameId,
                status: 'Preparing',
                slot: preparingSlot,
                participatingMembers,
                pendingCheckins,
                informedByBooking:
                  informedByBooking && typeof informedByBooking === 'object'
                    ? informedByBooking
                    : {},
              }
            : null;
          if (preparing) {
            if (preparingIndex >= 0) {
              next[preparingIndex] = { ...next[preparingIndex], ...preparing };
            } else {
              next.push(preparing);
            }
          } else if (preparingIndex >= 0) {
            next.splice(preparingIndex, 1);
          }
          return next;
        });
        // Handle late check-ins dialog reactively
        try {
          const pendingLate = Array.isArray(lateCheckins)
            ? lateCheckins.filter(x => x && x.status === 'pending')
            : [];
          if (pendingLate.length > 0) {
            setLateCheckinDialog(prev => ({ open: true, list: pendingLate }));
          } else if (lateCheckins && lateCheckins.length === 0) {
            setLateCheckinDialog(prev => (prev.open ? { open: false, list: [] } : prev));
          }
        } catch (_) {}
        // Merge persisted sit-outs and auto-reduce if check-ins exceed available spots
        try {
          if (sitOutByBooking && typeof sitOutByBooking === 'object') {
            setSitOutCounts(prev => ({
              ...prev,
              ...Object.fromEntries(
                Object.entries(sitOutByBooking).map(([k, v]) => [
                  String(k),
                  Math.max(0, Number(v) || 0),
                ])
              ),
            }));
          }
          // Build present counts by booking from participatingMembers
          const presentByBooking = new Map();
          for (const m of participatingMembers) {
            const idStr = String(typeof m === 'object' ? m?._id : m);
            const booking = memberIdToBooking.get(idStr);
            if (!booking) continue;
            const bid = String(booking._id);
            presentByBooking.set(bid, (presentByBooking.get(bid) || 0) + 1);
          }
          if (presentByBooking.size > 0) {
            setSitOutCounts(prev => {
              const next = { ...prev };
              const updates = [];
              for (const [bid, present] of presentByBooking.entries()) {
                const booking = gaBookings.find(b => String(b._id) === String(bid));
                if (!booking) continue;
                const total = getEffectiveParticipantCount(booking);
                const desired = Math.max(0, total - Math.max(0, Number(present) || 0));
                const current = Math.max(0, Number(next[bid] || 0));
                if (current > desired) {
                  next[bid] = desired;
                  updates.push({ bid, count: desired });
                }
              }
              // Persist reductions asynchronously
              if (updates.length > 0) {
                Promise.all(
                  updates.map(u =>
                    updateSitOutForPreparing({
                      bookingId: u.bid,
                      count: u.count,
                      entertainmentId,
                    })
                  )
                ).catch(() => {});
              }
              return next;
            });
          }
        } catch (_) {}
      } catch (e) {
        // silent to avoid noise
      }
    };
    load();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantsTick, activeId, selectedSlot]);

  // Manage auto-refresh lifecycle
  useEffect(() => {
    startParticipantsTimer();
    startAssignmentsTimer();
    return () => {
      stopParticipantsTimer();
      stopAssignmentsTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeId) {
      stopParticipantsTimer();
      stopAssignmentsTimer();
    } else {
      startParticipantsTimer();
      startAssignmentsTimer();
    }
  }, [activeId]);

  // Memoized values
  const activeGame = useMemo(() => games.find(g => g.status === 'Active'), [games]);

  // Fetch last ended game when no active game is running
  useEffect(() => {
    if (!entertainmentId || activeGame) {
      setLastEndedGame(null);
      return;
    }

    let cancelled = false;
    const fetchLastEndedGame = async () => {
      try {
        const now = dayjs();
        const fifteenMinutesAgo = now.subtract(15, 'minute');
        const sinceDate = fifteenMinutesAgo.toISOString();

        const successGames = await getLasertagGamesForOperator({
          entertainmentId,
          status: 'Success',
          since: sinceDate,
        });

        if (cancelled) return;

        if (successGames && successGames.length > 0) {
          const recentlyEnded = successGames
            .filter(g => g.endTime)
            .sort((a, b) => new Date(b.endTime) - new Date(a.endTime))[0];

          if (recentlyEnded) {
            const endTime = dayjs(recentlyEnded.endTime);
            // Double-check it's within 15 minutes
            if (endTime.isAfter(fifteenMinutesAgo)) {
              setLastEndedGame(recentlyEnded);
              return;
            }
          }
        }

        setLastEndedGame(null);
      } catch (err) {
        if (!cancelled) {
          setLastEndedGame(null);
        }
      }
    };

    fetchLastEndedGame();

    // Update every 30 seconds to fetch new games
    const fetchInterval = setInterval(fetchLastEndedGame, 30000);

    return () => {
      cancelled = true;
      clearInterval(fetchInterval);
    };
  }, [entertainmentId, activeGame]);

  // CountUp timer - update every second
  useEffect(() => {
    if (!lastEndedGame || activeGame) {
      setCountUpTick(0);
      return;
    }

    const interval = setInterval(() => {
      setCountUpTick(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [lastEndedGame, activeGame]);

  // Toggle slot color when game is started from backend (if not on 'all')
  useEffect(() => {
    const currentActiveGameId = activeGame?._id ? String(activeGame._id) : null;
    const previousActiveGameId = previousActiveGameIdRef.current;

    // Check if a new active game appeared (wasn't there before)
    if (
      currentActiveGameId &&
      currentActiveGameId !== previousActiveGameId &&
      !isStartingGame &&
      selectedSlot !== 'all'
    ) {
      // Toggle slot color when green/orange is selected
      if (selectedSlot === 'green') {
        setSelectedSlot('orange');
      } else if (selectedSlot === 'orange') {
        setSelectedSlot('green');
      }
    }

    // Update ref to track current active game
    previousActiveGameIdRef.current = currentActiveGameId;
  }, [activeGame?._id, isStartingGame, selectedSlot]);

  // Countdown calculation for auto-announcements (with active game or with last ended game for "after game end" triggers)
  useEffect(() => {
    if (!autoAnnouncementsConfig) {
      setBriefingCountdown(null);
      setGameStartCountdown(null);
      return;
    }

    const calculateCountdowns = () => {
      const now = dayjs();

      // Case 1: Active game – countdown to trigger before or (for game start) after this game ends
      if (activeGame) {
        const predictedEnd = activeGame.predictedEndTime
          ? dayjs(activeGame.predictedEndTime)
          : activeGame.startTime
            ? dayjs(activeGame.startTime).add(9, 'minute')
            : null;

        if (!predictedEnd) {
          setBriefingCountdown(null);
          setGameStartCountdown(null);
          return;
        }

        if (autoAnnouncementsConfig.briefing?.enabled) {
          const totalSecondsBefore = autoAnnouncementsConfig.briefing.totalSecondsBefore || 0;
          const isTriggered =
            autoAnnouncementsConfig.briefing?.triggeredForGameId === String(activeGame._id);
          if (totalSecondsBefore >= 0 && !isTriggered) {
            const triggerTime = predictedEnd.subtract(totalSecondsBefore, 'second');
            const diff = triggerTime.diff(now, 'second');
            setBriefingCountdown(diff > 0 ? diff : null);
          } else {
            setBriefingCountdown(null);
          }
        } else {
          setBriefingCountdown(null);
        }

        if (autoAnnouncementsConfig.gameStart?.enabled) {
          const totalSecondsBefore = autoAnnouncementsConfig.gameStart.totalSecondsBefore || 0;
          const isTriggered =
            autoAnnouncementsConfig.gameStart?.triggeredForGameId === String(activeGame._id);
          if (totalSecondsBefore >= 0 && !isTriggered) {
            const triggerTime = predictedEnd.subtract(totalSecondsBefore, 'second');
            const diff = triggerTime.diff(now, 'second');
            setGameStartCountdown(diff > 0 ? diff : null);
          } else if (totalSecondsBefore < 0 && !isTriggered && activeGame.endTime) {
            const endTime = dayjs(activeGame.endTime);
            const triggerTime = endTime.add(Math.abs(totalSecondsBefore), 'second');
            const diff = triggerTime.diff(now, 'second');
            setGameStartCountdown(diff > 0 ? diff : null);
          } else {
            setGameStartCountdown(null);
          }
        } else {
          setGameStartCountdown(null);
        }
        return;
      }

      // Case 2: No active game – countdown to "after game end" trigger using last ended game (mode "all")
      if (lastEndedGame?.endTime) {
        const lastId = String(lastEndedGame._id);

        if (autoAnnouncementsConfig.briefing?.enabled) {
          const totalSecondsBefore = autoAnnouncementsConfig.briefing.totalSecondsBefore ?? 0;
          const isTriggered = autoAnnouncementsConfig.briefing?.triggeredForGameId === lastId;
          if (totalSecondsBefore < 0 && !isTriggered) {
            const triggerTime = dayjs(lastEndedGame.endTime).add(
              Math.abs(totalSecondsBefore),
              'second'
            );
            const diff = triggerTime.diff(now, 'second');
            setBriefingCountdown(diff > 0 ? diff : null);
          } else {
            setBriefingCountdown(null);
          }
        } else {
          setBriefingCountdown(null);
        }

        if (autoAnnouncementsConfig.gameStart?.enabled) {
          const totalSecondsBefore = autoAnnouncementsConfig.gameStart.totalSecondsBefore ?? 0;
          const isTriggered = autoAnnouncementsConfig.gameStart?.triggeredForGameId === lastId;
          if (totalSecondsBefore < 0 && !isTriggered) {
            const triggerTime = dayjs(lastEndedGame.endTime).add(
              Math.abs(totalSecondsBefore),
              'second'
            );
            const diff = triggerTime.diff(now, 'second');
            setGameStartCountdown(diff > 0 ? diff : null);
          } else {
            setGameStartCountdown(null);
          }
        } else {
          setGameStartCountdown(null);
        }
      } else {
        setBriefingCountdown(null);
        setGameStartCountdown(null);
      }
    };

    calculateCountdowns();
    const interval = setInterval(calculateCountdowns, 1000);
    return () => clearInterval(interval);
  }, [
    activeGame?._id,
    activeGame?.predictedEndTime,
    activeGame?.startTime,
    activeGame?.endTime,
    lastEndedGame?._id,
    lastEndedGame?.endTime,
    autoAnnouncementsConfig?.briefing?.enabled,
    autoAnnouncementsConfig?.briefing?.totalSecondsBefore,
    autoAnnouncementsConfig?.briefing?.triggeredForGameId,
    autoAnnouncementsConfig?.gameStart?.enabled,
    autoAnnouncementsConfig?.gameStart?.totalSecondsBefore,
    autoAnnouncementsConfig?.gameStart?.triggeredForGameId,
  ]);

  // Format countdown seconds to MM:SS
  const formatCountdown = seconds => {
    if (seconds <= 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const preparingById = useMemo(() => {
    const result = { 0: null, 1: null };
    for (const g of games) {
      if (g.status !== 'Preparing') continue;
      const id = normalizeSlotId(typeof g?.slotId === 'number' ? g.slotId : g.slot);
      if (id === 0 || id === 1) {
        // choose the first for each slot id (or keep any existing)
        if (!result[id]) result[id] = g;
      }
    }
    return result;
  }, [games]);

  // Determine selected slot id from UI selection
  const selectedSlotId = useMemo(() => {
    if (selectedSlot === 'green') return 1;
    if (selectedSlot === 'orange') return 0;
    return null; // 'all'
  }, [selectedSlot]);

  const currentPreparingGame = useMemo(() => {
    const bySlot = preparingById[0] || preparingById[1] || null;
    if (bySlot) return bySlot;
    // fallback: any preparing game even if slot undefined
    const anyPreparing = games.find(g => g.status === 'Preparing');
    return anyPreparing || null;
  }, [preparingById, games]);
  const currentPreparingSlotId = useMemo(() => {
    return currentPreparingGame
      ? normalizeSlotId(
          typeof currentPreparingGame?.slotId === 'number'
            ? currentPreparingGame.slotId
            : currentPreparingGame.slot
        )
      : null;
  }, [currentPreparingGame]);

  // Calculate countUp data for last ended game
  const countUpData = useMemo(() => {
    if (!lastEndedGame || activeGame || !lastEndedGame.endTime) return null;

    const now = dayjs();
    const endTime = dayjs(lastEndedGame.endTime);
    const fifteenMinutesAgo = now.subtract(15, 'minute');

    // Check if still within 15 minutes
    if (!endTime.isAfter(fifteenMinutesAgo)) {
      return null;
    }

    const endedSecondsAgo = Math.max(0, now.diff(endTime, 'second'));
    const endedMinutesAgo = Math.floor(endedSecondsAgo / 60);
    const endedSeconds = endedSecondsAgo % 60;

    // Red color if >= 5 minutes
    const isRed = endedMinutesAgo >= 5;

    const slotId = normalizeSlotId(
      typeof lastEndedGame.slotId === 'number' ? lastEndedGame.slotId : lastEndedGame.slot
    );
    const color = getSlotColorById(slotId, theme);
    const slotLabel = getSlotLabelById(slotId);

    return {
      endedSecondsAgo,
      endedMinutesAgo,
      endedSeconds,
      isRed,
      slotId,
      color,
      slotLabel,
    };
  }, [lastEndedGame, activeGame, countUpTick, theme]);

  // Load operator context (bookings, games, teamAssignment) periodically
  useEffect(() => {
    const f = async () => {
      if (activeId) {
        return;
      }
      if (!entertainmentId) return;
      setGaLoading(true);
      const epochAtStart = dragEpochRef.current;
      try {
        const slotParam = getSlotParamForRequests();
        const response = await getOperatorContext({ entertainmentId, slot: slotParam });
        if (response && dragEpochRef.current === epochAtStart && !activeId) {
          const {
            bookings = [],
            activeGame: ag = null,
            preparingGame: pg = null,
            activeUnassigned: au = { unassignedBookings: [], unassignedMembers: [] },
          } = response || {};
          setGaBookings(bookings);
          setActiveUnassigned(au);
          // Normalize assignment from backend
          let normalized = null;
          if (pg && pg.teamAssignment && typeof pg.teamAssignment === 'object') {
            normalized = pg.teamAssignment;
          }
          setTeamAssignmentFull(normalized);
          const list = [];
          if (pg) list.push(pg);
          if (ag) list.push(ag);
          setGames(list);
          // Context no longer controls late check-in dialog to avoid flicker; participants poll handles it
          // Initialize selected team count from backend once
          if (
            !hasInitializedTeamCountRef.current &&
            normalized &&
            typeof normalized.teamCount === 'number'
          ) {
            hasInitializedTeamCountRef.current = true;
            setTeamCount(Number(normalized.teamCount));
          }
          // Flatten assignments for current teamCount
          const flat = (normalized?.assignments || [])
            .filter(
              a =>
                Number(a.teamNumber) >= 1 &&
                Number(a.teamNumber) <= Number(normalized?.teamCount || teamCount)
            )
            .map(a => ({
              bookingId: a.bookingId,
              teamNumber: a.teamNumber,
              teamCount: Number(normalized?.teamCount || teamCount),
            }));
          setGroupAssignments(flat);
        }
      } catch (e) {
        toast.error('Fehler beim Laden der Teameinteilung');
      } finally {
        setGaLoading(false);
      }
    };
    f();
  }, [entertainmentId, assignmentsTick, activeId, selectedSlot]);

  // Recompute flat assignments when teamCount changes
  useEffect(() => {
    const assignments = teamAssignmentFull?.assignments || [];
    const flat = assignments
      .filter(a => Number(a.teamNumber) >= 1 && Number(a.teamNumber) <= Number(teamCount))
      .map(a => ({
        bookingId: a.bookingId,
        teamNumber: a.teamNumber,
        teamCount: Number(teamCount),
      }));
    setGroupAssignments(flat);
  }, [teamAssignmentFull, teamCount]);

  // Initialize/merge sit-out counts from bookings without overwriting local state when backend doesn't provide it
  useEffect(() => {
    try {
      setSitOutCounts(prev => {
        const next = { ...prev };
        for (const b of gaBookings) {
          const id = String(b?._id || '');
          if (!id) continue;
          const raw = b?.ltSpecific?.sitOutNextGame;
          if (typeof raw === 'number' && raw >= 0) {
            next[id] = Math.max(0, Number(raw));
          }
        }
        return next;
      });
    } catch (_) {}
  }, [gaBookings]);

  // Load availability of last assignment by selected slot
  useEffect(() => {
    if (!entertainmentId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const param = getSlotParamForRequests();
        const resp = await getLastAssignmentBySlot({ slot: param, entertainmentId });
        if (cancelled) return;
        if (
          resp &&
          resp.exists &&
          resp.teamAssignment &&
          Array.isArray(resp.teamAssignment.assignments)
        ) {
          setLastReuseAvailable(true);
          setLastReuseTemplate({
            teamCount: Number(resp.teamAssignment.teamCount) || 2,
            assignments: resp.teamAssignment.assignments.map(a => ({
              bookingId: String(a.bookingId),
              teamNumber: Number(a.teamNumber),
            })),
          });
        } else {
          setLastReuseAvailable(false);
          setLastReuseTemplate(null);
        }
      } catch (_) {
        setLastReuseAvailable(false);
        setLastReuseTemplate(null);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedSlot, assignmentsTick, entertainmentId]);

  const renderActiveGameCard = game => {
    if (!game) return null;
    const slotId = normalizeSlotId(typeof game?.slotId === 'number' ? game.slotId : game.slot);
    const color = getSlotColorById(slotId, theme);

    // Get team assignment for the active game
    const currentTeamAssignment = game.teamAssignment || null;
    const currentTeamCount = currentTeamAssignment?.teamCount || 2;

    // Create participants by booking map for active game
    const participantsByBookingForActive = (() => {
      const map = new Map();
      if (!game.participatingMembers || !Array.isArray(game.participatingMembers)) return map;
      for (const member of game.participatingMembers) {
        const memberId = String(typeof member === 'object' ? member?._id : member);
        const booking = memberIdToBooking.get(memberId);
        if (!booking) continue;
        const bookingId = String(booking._id);
        if (!map.has(bookingId)) map.set(bookingId, []);
        const pmList = Array.isArray(booking?.ltSpecific?.participatingMembers)
          ? booking.ltSpecific.participatingMembers
          : [];
        let memberMeta = pmList.find(
          pm => String(typeof pm === 'string' ? pm : pm?._id) === memberId
        );
        if (typeof memberMeta === 'string') {
          memberMeta = { _id: memberMeta };
        }
        map.get(bookingId).push(memberMeta || { _id: memberId });
      }
      return map;
    })();
    // Get bookings assigned to teams
    const teamBookings = (() => {
      const teams = {};
      for (let i = 1; i <= currentTeamCount; i++) teams[i] = [];
      if (currentTeamAssignment && Array.isArray(currentTeamAssignment.assignments)) {
        const assignments = currentTeamAssignment.assignments || [];
        for (const assignment of assignments) {
          const booking = gaBookings.find(b => String(b._id) === String(assignment.bookingId));
          if (!booking) continue;
          if (assignment.teamNumber >= 1 && assignment.teamNumber <= currentTeamCount) {
            teams[assignment.teamNumber].push(booking);
          }
        }
      }
      return teams;
    })();

    // Get unassigned bookings (checked in but not assigned to any team)
    const unassignedBookings = (() => {
      const unassignedBookingIds = new Set(
        (activeUnassigned?.unassignedBookings || []).map(id => String(id))
      );
      return gaBookings.filter(b => unassignedBookingIds.has(String(b._id)));
    })();

    // Build mapping of booking -> sorted team numbers it is assigned to (for split handling)
    const assignedTeamsByBooking = (() => {
      const map = new Map();
      if (currentTeamAssignment && Array.isArray(currentTeamAssignment.assignments)) {
        for (const a of currentTeamAssignment.assignments) {
          const bid = String(a.bookingId);
          const tn = Number(a.teamNumber);
          if (!map.has(bid)) map.set(bid, new Set());
          map.get(bid).add(tn);
        }
      }
      const result = new Map();
      for (const [bid, set] of map.entries()) {
        result.set(
          bid,
          Array.from(set).sort((x, y) => x - y)
        );
      }
      return result;
    })();

    // Evenly allocate a booking's count across the teams it is assigned to
    const allocateEvenSplitCount = (total, teams, teamNumber) => {
      try {
        const k = Array.isArray(teams) ? teams.length : 1;
        if (!k || k <= 1) return Math.max(0, Number(total) || 0);
        const idx = teams.indexOf(Number(teamNumber));
        if (idx === -1) return 0;
        const base = Math.floor((Number(total) || 0) / k);
        const rem = (Number(total) || 0) % k;
        return base + (idx < rem ? 1 : 0);
      } catch (_) {
        return Math.max(0, Number(total) || 0);
      }
    };

    // Reconstruct split quantities per booking using current loads to avoid double counting
    const reconstructedSplitTotals = (() => {
      const sizeById = new Map();
      for (const b of gaBookings) {
        sizeById.set(String(b._id), getAdjustedCountWithMap(b, activeSitOutCounts));
      }
      const loads = new Map();
      for (let i = 1; i <= currentTeamCount; i++) loads.set(i, 0);
      // Seed loads with single-assigned bookings
      if (currentTeamAssignment && Array.isArray(currentTeamAssignment.assignments)) {
        const byBooking = new Map();
        for (const a of currentTeamAssignment.assignments) {
          const bid = String(a.bookingId);
          if (!byBooking.has(bid)) byBooking.set(bid, new Set());
          byBooking.get(bid).add(Number(a.teamNumber));
        }
        for (const [bid, teams] of byBooking.entries()) {
          const arr = Array.from(teams);
          const size = Number(sizeById.get(bid) || 0);
          if (arr.length === 1) {
            loads.set(arr[0], (loads.get(arr[0]) || 0) + size);
          }
        }
      }
      const allocation = new Map();
      // Allocate for split bookings greedily to the currently lightest teams
      for (const [bid, teams] of assignedTeamsByBooking.entries()) {
        if (!Array.isArray(teams) || teams.length <= 1) continue;
        const total = Number(sizeById.get(bid) || 0);
        if (total <= 0) continue;
        const k = teams.length;
        const counts = new Array(k).fill(0);
        let remaining = total;
        // Ensure at least 1 per team if possible
        if (remaining >= k) {
          for (let i = 0; i < k; i++) {
            counts[i] += 1;
            loads.set(teams[i], (loads.get(teams[i]) || 0) + 1);
            remaining -= 1;
          }
        } else {
          // If fewer participants than teams, fill the lightest teams with 1 until we run out
          const order = [...teams].sort((a, b) => (loads.get(a) || 0) - (loads.get(b) || 0));
          for (let i = 0; i < remaining; i++) {
            const t = order[i];
            const idx = teams.indexOf(t);
            counts[idx] += 1;
          }
          remaining = 0;
        }
        // Greedily distribute the rest
        while (remaining > 0) {
          let bestIdx = 0;
          let bestLoad = loads.get(teams[0]) || 0;
          for (let i = 1; i < k; i++) {
            const l = loads.get(teams[i]) || 0;
            if (l < bestLoad) {
              bestLoad = l;
              bestIdx = i;
            }
          }
          counts[bestIdx] += 1;
          loads.set(teams[bestIdx], (loads.get(teams[bestIdx]) || 0) + 1);
          remaining -= 1;
        }
        const map = {};
        for (let i = 0; i < k; i++) map[teams[i]] = counts[i];
        allocation.set(bid, map);
      }
      return allocation; // Map(bid -> { teamNumber: count })
    })();

    const getAllocatedTotalFor = (booking, teamNumber) => {
      const bid = String(booking._id);
      const manual = reconstructedSplitTotals.get(bid);
      if (manual && manual[teamNumber] != null) return Number(manual[teamNumber]) || 0;
      // Fallback to even split if not reconstructed
      const assignedTeams = assignedTeamsByBooking.get(bid) || [teamNumber];
      const total = getAdjustedCountWithMap(booking, activeSitOutCounts);
      return allocateEvenSplitCount(total, assignedTeams, teamNumber);
    };

    const getAllocatedCheckedInFor = (booking, teamNumber) => {
      const bid = String(booking._id);
      const totalCheckedInRaw = (participantsByBookingForActive.get(bid) || []).filter(
        pm => !isPlaceholderMember(pm)
      ).length;
      const allowedTotal = getAdjustedCountWithMap(booking, activeSitOutCounts);
      const totalCheckedIn = Math.min(totalCheckedInRaw, allowedTotal);
      // Distribute proportionally to allocated totals
      const assignedTeams = assignedTeamsByBooking.get(bid) || [teamNumber];
      const allocTotals = assignedTeams.map(tn => getAllocatedTotalFor(booking, tn));
      const sum = allocTotals.reduce((s, v) => s + v, 0);
      if (sum <= 0) return 0;
      const fractions = allocTotals.map(v => (v / sum) * totalCheckedIn);
      const bases = fractions.map(f => Math.floor(f));
      let leftover = Math.max(0, totalCheckedIn - bases.reduce((s, v) => s + v, 0));
      const fracs = fractions.map((f, i) => ({ i, frac: f - bases[i] }));
      fracs.sort((a, b) => b.frac - a.frac);
      for (let k = 0; k < fracs.length && leftover > 0; k++) {
        bases[fracs[k].i] += 1;
        leftover -= 1;
      }
      const idx = assignedTeams.indexOf(Number(teamNumber));
      return idx >= 0 ? bases[idx] : 0;
    };

    // Allocate the number of sit-outs of a booking to a specific team proportionally
    const getAllocatedSitOutFor = (booking, teamNumber) => {
      try {
        const bid = String(booking._id);
        const base = getEffectiveParticipantCount(booking);
        const adjusted = getAdjustedCountWithMap(booking, activeSitOutCounts);
        const sitOutTotal = Math.max(0, base - adjusted);
        const assignedTeams = assignedTeamsByBooking.get(bid) || [teamNumber];
        if (assignedTeams.length <= 1) return sitOutTotal;
        const allocTotals = assignedTeams.map(tn => getAllocatedTotalFor(booking, tn));
        const sum = allocTotals.reduce((s, v) => s + v, 0);
        if (sum <= 0) {
          // Even split if no allocation basis exists
          const k = assignedTeams.length;
          const baseEach = Math.floor(sitOutTotal / k);
          let leftover = sitOutTotal - baseEach * k;
          const idx = assignedTeams.indexOf(Number(teamNumber));
          return baseEach + (idx >= 0 && idx < leftover ? 1 : 0);
        }
        const fractions = allocTotals.map(v => (v / sum) * sitOutTotal);
        const bases = fractions.map(f => Math.floor(f));
        let leftover = Math.max(0, sitOutTotal - bases.reduce((s, v) => s + v, 0));
        const fracs = fractions.map((f, i) => ({ i, frac: f - bases[i] }));
        fracs.sort((a, b) => b.frac - a.frac);
        for (let k = 0; k < fracs.length && leftover > 0; k++) {
          bases[fracs[k].i] += 1;
          leftover -= 1;
        }
        const idx = assignedTeams.indexOf(Number(teamNumber));
        return idx >= 0 ? bases[idx] : 0;
      } catch (_) {
        return 0;
      }
    };

    // Allocate number of not-attended (placeholders) for a booking to a specific team
    const getAllocatedNotAttendedFor = (booking, teamNumber) => {
      try {
        const bid = String(booking._id);
        const placeholders = Math.max(0, Number(getPlaceholdersCount(booking)) || 0);
        const assignedTeams = assignedTeamsByBooking.get(bid) || [teamNumber];
        if (assignedTeams.length <= 1) return placeholders;
        const allocTotals = assignedTeams.map(tn => getAllocatedTotalFor(booking, tn));
        const sum = allocTotals.reduce((s, v) => s + v, 0);
        if (sum <= 0) {
          const k = assignedTeams.length;
          const baseEach = Math.floor(placeholders / k);
          let leftover = placeholders - baseEach * k;
          const idx = assignedTeams.indexOf(Number(teamNumber));
          return baseEach + (idx >= 0 && idx < leftover ? 1 : 0);
        }
        const fractions = allocTotals.map(v => (v / sum) * placeholders);
        const bases = fractions.map(f => Math.floor(f));
        let leftover = Math.max(0, placeholders - bases.reduce((s, v) => s + v, 0));
        const fracs = fractions.map((f, i) => ({ i, frac: f - bases[i] }));
        fracs.sort((a, b) => b.frac - a.frac);
        for (let k = 0; k < fracs.length && leftover > 0; k++) {
          bases[fracs[k].i] += 1;
          leftover -= 1;
        }
        const idx = assignedTeams.indexOf(Number(teamNumber));
        return idx >= 0 ? bases[idx] : 0;
      } catch (_) {
        return 0;
      }
    };

    return (
      <Card
        variant='outlined'
        sx={{
          borderColor: color,
          borderWidth: 2,
          background: `linear-gradient(135deg, ${color}22 0%, ${color}11 60%, transparent 100%)`,
        }}
      >
        <CardHeader
          title={
            <Typography variant='h6' sx={{ color }}>
              Laufendes Spiel – {getSlotLabelById(slotId)}
            </Typography>
          }
          subheader={
            <Typography variant='body2' color='text.secondary'>
              Spieler: {game.participatingMembers?.length || 0}
            </Typography>
          }
        />
        <Divider />
        <CardContent>
          {/* moved player count to CardHeader subheader */}

          {/* Team assignments display */}
          {currentTeamCount > 0 && (
            <Box>
              <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 600 }}>
                Teamaufteilung ({currentTeamCount} Teams)
              </Typography>
              <Box
                display='grid'
                gridTemplateColumns={{
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md:
                    currentTeamCount === 3 || currentTeamCount === 6
                      ? 'repeat(3, 1fr)'
                      : 'repeat(2, 1fr)',
                }}
                gap={2}
              >
                {teamColors.slice(0, currentTeamCount).map(team => {
                  const teamBookingsList = teamBookings[team.number] || [];
                  const teamBorder = team.borderColor || team.color;

                  // Calculate team check-in status for active game
                  const activeTeamCheckInStatus = (() => {
                    let totalParticipants = 0;
                    let checkedInParticipants = 0;
                    let sitOutParticipants = 0;
                    let notAttendedParticipants = 0;

                    for (const booking of teamBookingsList) {
                      const allocTotal = getAllocatedTotalFor(booking, team.number);
                      const allocCheckedIn = getAllocatedCheckedInFor(booking, team.number);
                      totalParticipants += allocTotal;
                      checkedInParticipants += allocCheckedIn;
                      sitOutParticipants += getAllocatedSitOutFor(booking, team.number);
                      notAttendedParticipants += getAllocatedNotAttendedFor(booking, team.number);
                    }

                    return {
                      totalParticipants,
                      checkedInParticipants,
                      sitOutParticipants,
                      notAttendedParticipants,
                      allCheckedIn:
                        totalParticipants > 0 && checkedInParticipants === totalParticipants,
                    };
                  })();

                  return (
                    <Paper
                      key={team.number}
                      sx={{
                        p: 2,
                        border: `2px solid ${teamBorder}`,
                        borderRadius: 2,
                        backgroundColor: `${team.color}10`,
                      }}
                    >
                      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            minWidth: 12,
                            minHeight: 12,
                            flexShrink: 0,
                            flex: '0 0 auto',
                            borderRadius: '50%',
                            backgroundColor: team.color,
                            ...(isHexWhiteLike(team.color)
                              ? { border: `1px solid ${teamBorder}` }
                              : {}),
                          }}
                        />
                        <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                          Team {team.name}
                        </Typography>
                        {/* no checkmark icon for active game team header */}
                        {(() => {
                          const parts = [];
                          const notCheckedIn = Math.max(
                            0,
                            activeTeamCheckInStatus.totalParticipants -
                              activeTeamCheckInStatus.checkedInParticipants
                          );
                          parts.push(`${activeTeamCheckInStatus.checkedInParticipants} spielen`);
                          if (notCheckedIn > 0) parts.push(`${notCheckedIn} nicht eingecheckt`);
                          if (activeTeamCheckInStatus.sitOutParticipants > 0)
                            parts.push(
                              `${
                                activeTeamCheckInStatus.sitOutParticipants > 1
                                  ? activeTeamCheckInStatus.sitOutParticipants + ' setzen aus'
                                  : '1 setzt aus'
                              }`
                            );
                          return (
                            <Typography variant='body2' color='text.secondary'>
                              ({parts.join(', ')})
                            </Typography>
                          );
                        })()}
                      </Stack>
                      <Box sx={{ overflowY: 'auto', maxHeight: 300 }}>
                        {teamBookingsList.map(booking => {
                          const bid = String(booking._id);
                          const isSplit = (assignedTeamsByBooking.get(bid) || []).length > 1;
                          const allocatedTotal = getAllocatedTotalFor(booking, team.number);
                          const allocatedSitOut = getAllocatedSitOutFor(booking, team.number);
                          const allocatedNotAttended = getAllocatedNotAttendedFor(
                            booking,
                            team.number
                          );
                          const allocatedCheckedIn = getAllocatedCheckedInFor(booking, team.number);
                          return (
                            <BookingCard
                              key={booking._id}
                              booking={booking}
                              participants={
                                participantsByBookingForActive.get(String(booking._id)) || []
                              }
                              slotId={slotId}
                              hideWarnings
                              hideCheckedInSummary
                              hideCheckIcons
                              onlyShowCheckedIn
                              isSplit={isSplit}
                              splitCount={allocatedTotal}
                              teamNumber={team.number}
                              // Provide allocated not-attended and sit-out counts for active display block
                              allocatedNotAttended={allocatedNotAttended}
                              sitOutDisplayCount={allocatedSitOut}
                              allocatedCheckedIn={allocatedCheckedIn}
                              onOpenDetails={handleOpenDetails}
                            />
                          );
                        })}
                      </Box>
                    </Paper>
                  );
                })}

                {/* Unassigned team (checked in but not assigned) */}
                {unassignedBookings.length > 0 && (
                  <Paper
                    sx={{
                      p: 2,
                      border: theme => `2px solid ${theme.palette.grey[500]}`,
                      borderRadius: 2,
                      backgroundColor: theme => alpha(theme.palette.grey[500], 0.082),
                    }}
                  >
                    <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          minWidth: 12,
                          minHeight: 12,
                          flexShrink: 0,
                          flex: '0 0 auto',
                          borderRadius: '50%',
                          backgroundColor: theme.palette.grey[500],
                        }}
                      />
                      <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                        Unzugewiesen
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        (
                        {unassignedBookings.reduce((sum, b) => {
                          const participants =
                            participantsByBookingForActive.get(String(b._id)) || [];
                          return sum + participants.length;
                        }, 0)}{' '}
                        eingecheckt)
                      </Typography>
                    </Stack>
                    <Box sx={{ overflowY: 'auto', maxHeight: 300 }}>
                      {unassignedBookings.map(booking => {
                        const participants =
                          participantsByBookingForActive.get(String(booking._id)) || [];
                        return (
                          <BookingCard
                            key={booking._id}
                            booking={booking}
                            participants={participants}
                            slotId={slotId}
                            hideWarnings
                            hideCheckedInSummary
                            hideCheckIcons
                            onlyShowCheckedIn
                            onOpenDetails={handleOpenDetails}
                          />
                        );
                      })}
                    </Box>
                  </Paper>
                )}
              </Box>
            </Box>
          )}
        </CardContent>
        {(() => {
          const gameDurationMinutes = 9;
          const startedAt = game?.startTime ? dayjs(game.startTime) : null;
          const endsAt = game?.predictedEndTime
            ? dayjs(game.predictedEndTime)
            : startedAt.add(GAME_DURATION_MIN, 'minute');
          const now = dayjs();
          const remainingMinutes = (() => {
            try {
              if (!startedAt || !startedAt.isValid()) return null;
              const diff = Math.ceil(endsAt.diff(now, 'minute', true));
              return Math.max(0, diff);
            } catch (_) {
              return null;
            }
          })();
          const completionPercent = (() => {
            try {
              if (remainingMinutes == null) return null;
              const elapsed = now.diff(startedAt, 'minute', true);
              const duration = endsAt.diff(startedAt, 'minute', true);
              return Math.min(100, Math.round((elapsed / duration) * 100));
            } catch (_) {
              return null;
            }
          })();
          return (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'grid',
                alignItems: 'center',
                gridTemplateColumns: '1fr auto',
                gap: 2,
                backgroundColor: `${color}08`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant='body2'>
                  {remainingMinutes > 0
                    ? 'Geschätzte verbleibende Spieldauer: ' + remainingMinutes + ' Minuten'
                    : 'Spiel voraussichtlich beendet'}
                </Typography>
                {completionPercent != null && completionPercent < 100 && (
                  <Box sx={{ minWidth: 140, maxWidth: 220, flexShrink: 0 }}>
                    <LinearProgress
                      variant='determinate'
                      value={completionPercent}
                      sx={{
                        height: 8,
                        borderRadius: 1,
                        backgroundColor: `${color}22`,
                        '& .MuiLinearProgress-bar': { backgroundColor: color },
                      }}
                    />
                  </Box>
                )}
              </Box>
              {canControlGames && (
                <Button
                  variant='contained'
                  color='error'
                  size='small'
                  disabled={!game || isEndingGame}
                  onClick={() => setEndConfirmDialog({ open: true, game })}
                  sx={{
                    minWidth: { xs: 'auto', sm: '120px' },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    px: { xs: 1, sm: 2 },
                    justifySelf: 'end',
                  }}
                >
                  Spiel beenden
                </Button>
              )}
            </Box>
          );
        })()}
      </Card>
    );
  };

  // Booking DnD components reused from TeamAssignment
  const BookingCard = memo(
    ({
      booking,
      onUnassign,
      participants = [],
      slotId,
      hideWarnings = false,
      hideCheckedInSummary = false,
      hideCheckIcons = false,
      onlyShowCheckedIn = false,
      canSplit = false,
      onSplit = null,
      isSplit = false,
      onMerge = null,
      teamNumber = null,
      splitCount = null,
      onChangeSplitCount = null,
      onOpenDetails = null,
      sitOutCount = 0,
      onChangeSitOutCount = null,
      // optional: display sit-outs (used in active game view)
      sitOutDisplayCount = null,
      // optional: informed flag for preparing game (collapsed state)
      isInformed = false,
      onInformedChange = null,
      // optional: allocated not-attended (used in active game view)
      allocatedNotAttended = null,
      // optional: allocated checked-in (used in active game view)
      allocatedCheckedIn = null,
    }) => {
      const memberBookingSlot = booking?.ltSpecific?.playerGroupID;
      // Do not show a mismatch when active slot covers all (2)
      const showMismatchForBooking =
        slotId != null && slotId !== 2 && memberBookingSlot != null && memberBookingSlot !== slotId;
      const showNotBriefedForBooking = booking && !booking?.ltSpecific?.briefedAt;
      const showNotPresentForBooking = booking && !booking?.presentSince;
      const showMissingSlotWarning =
        booking &&
        (booking?.ltSpecific?.playerGroupID === undefined ||
          booking?.ltSpecific?.playerGroupID === null);
      const isExpanded = expandedGroups.has(booking._id);
      const totalCount = getAdjustedEffectiveParticipantCount(booking);
      const bookedTotalCount = Number(booking.participantCount) || 0;
      const checkedInCount = participants.length;
      const presentCount = Array.isArray(participants)
        ? participants.filter(pm => !isPlaceholderMember(pm)).length
        : 0;
      // A booking gets its check mark only when all participants of the booking are present
      const allCheckedIn = presentCount > 0 && presentCount === totalCount;

      const toggleExpanded = () => {
        setExpandedGroups(prev => {
          const newSet = new Set(prev);
          if (newSet.has(booking._id)) {
            newSet.delete(booking._id);
          } else {
            newSet.add(booking._id);
          }
          return newSet;
        });
      };

      const timingInfo = getTimingInfoForBookingDetailed(booking);
      const playedEnough = hasPlayedEnoughGames(booking);

      return (
        <Paper
          sx={{
            p: 2,
            mb: 1,
            borderRadius: 2,
            boxShadow: 3,
            opacity: isInformed ? 0.65 : 1,
            transition: 'opacity 0.2s ease',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
              minWidth: 0,
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                flex: 1,
                flexWrap: 'wrap',
                minWidth: 0,
              }}
            >
              {!hideCheckIcons && allCheckedIn && (
                <CheckCircle sx={{ fontSize: 18, color: 'success.main', mt: 0.5 }} />
              )}
              <Typography
                variant='subtitle1'
                sx={{ fontWeight: 600, flex: '1 1 auto', minWidth: 0 }}
              >
                {booking.firstName} {booking.lastName}
              </Typography>
              {booking.ltSpecific?.groupName && (
                <Typography
                  variant='body1'
                  sx={{
                    fontStyle: 'italic',
                    fontWeight: 500,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: 'action.hover',
                  }}
                >
                  „<GroupNameDisplay name={booking.ltSpecific.groupName} />“
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, flexShrink: 0 }}>
                <IconButton
                  size='small'
                  onMouseDown={e => {
                    e.stopPropagation();
                  }}
                  onTouchStart={e => {
                    e.stopPropagation();
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    onOpenDetails(booking);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <OpenInNewIcon fontSize='small' />
                </IconButton>
                <Box sx={{ flexShrink: 0, mt: 0.25 }}>
                  <NumberChip
                    number={
                      isSplit && typeof splitCount === 'number'
                        ? Math.min(splitCount, getAdjustedEffectiveParticipantCount(booking))
                        : getAdjustedEffectiveParticipantCount(booking)
                    }
                    size='small'
                    color='primary'
                  />
                </Box>
              </Box>
              {(() => {
                const gamesPlayedCount = Number(booking?.ltSpecific?.gamesPlayedCount || 0);
                return (
                  <Box
                    sx={{
                      flexBasis: '100%',
                      mt: 0.25,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    {timingInfo && (
                      <Typography
                        variant='caption'
                        sx={{
                          color: timingInfo.isBefore
                            ? 'error.main'
                            : timingInfo.isAfter
                              ? 'error.main'
                              : 'success.main',
                          fontWeight: 500,
                        }}
                      >
                        {timingInfo.label}
                      </Typography>
                    )}
                    {timingInfo && (
                      <Typography variant='caption' color='text.secondary'>
                        -
                      </Typography>
                    )}
                    <Typography
                      variant='caption'
                      sx={{ color: playedEnough ? 'success.main' : 'text.secondary' }}
                    >
                      Spiele: {gamesPlayedCount}
                      {playedEnough && (
                        <CheckCircle sx={{ fontSize: 12, ml: 0.25, verticalAlign: 'middle' }} />
                      )}
                    </Typography>
                  </Box>
                );
              })()}
              {!hideCheckedInSummary && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexBasis: '100%',
                    mt: 0.25,
                  }}
                >
                  <Typography variant='body2' color='text.secondary'>
                    {`(${checkedInCount}/${totalCount} eingecheckt)`}
                  </Typography>
                </Box>
              )}

              {/* Active game block (card header area) removed sit-out chip; details shown below */}
            </Box>
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              sx={{ flexWrap: 'wrap', alignSelf: 'flex-start' }}
            >
              {isSplit ? null : onUnassign ? (
                <IconButton
                  size='small'
                  onClick={e => {
                    e.stopPropagation();
                    onUnassign();
                  }}
                  sx={{ py: 0.5, order: 0 }}
                >
                  <Close fontSize='small' />
                </IconButton>
              ) : null}
              {isSplit && (
                <Stack direction='row' alignItems='center' spacing={0.25}>
                  {onMerge ? (
                    <Tooltip title='Zusammenführen'>
                      <IconButton
                        size='small'
                        onClick={e => {
                          e.stopPropagation();
                          onMerge(booking);
                        }}
                      >
                        <CallMerge fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Stack>
              )}
              {/* expand moved to bottom as full-width control */}
              {canSplit && onSplit ? (
                <Button
                  size='small'
                  variant='outlined'
                  color='primary'
                  onClick={e => {
                    e.stopPropagation();
                    onSplit(booking);
                  }}
                >
                  Aufteilen
                </Button>
              ) : null}
              {/* merge icon now lives next to the Aufgeteilt label */}
            </Stack>
          </Box>

          {/* Booking-level warnings */}
          {/* Split quantity controls for split bookings inside teams */}
          {isSplit && typeof teamNumber === 'number' && splitCount != null && (
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                Zu diesem Team: {splitCount} von {totalCount}
              </Typography>
              {onChangeSplitCount && (
                <ButtonGroup size='small' variant='outlined'>
                  <Button
                    onClick={e => {
                      e.stopPropagation();
                      onChangeSplitCount(Math.max(0, splitCount - 1), totalCount);
                    }}
                  >
                    -
                  </Button>
                  <Button
                    onClick={e => {
                      e.stopPropagation();
                      onChangeSplitCount(Math.min(totalCount, splitCount + 1), totalCount);
                    }}
                  >
                    +
                  </Button>
                </ButtonGroup>
              )}
            </Box>
          )}

          {/* Booking-level warnings */}
          {!hideWarnings &&
            (showMismatchForBooking ||
              showNotBriefedForBooking ||
              showNotPresentForBooking ||
              showMissingSlotWarning) && (
              <Box sx={{ mb: 1 }}>
                {showMismatchForBooking && (
                  <Alert
                    severity='error'
                    sx={{
                      textTransform: 'none',
                      opacity: 0.9,
                      p: 0.5,
                      mb: showNotBriefedForBooking || showNotPresentForBooking ? 0.5 : 0,
                    }}
                  >
                    <Box component='span' sx={{ fontWeight: 600 }}>
                      Falscher Slot
                    </Box>{' '}
                    – Buchung im {memberBookingSlot === 1 ? 'grünen' : 'orangenen'} Slot
                  </Alert>
                )}
                {showNotBriefedForBooking && (
                  <Alert
                    severity='warning'
                    sx={{
                      textTransform: 'none',
                      opacity: 0.9,
                      p: 0.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant='body2' sx={{ mr: 1 }}>
                        Briefing fehlt
                      </Typography>
                      <Button
                        size='small'
                        variant='outlined'
                        color='warning'
                        onClick={async e => {
                          e.stopPropagation();
                          try {
                            await markLTCustomerAsBriefed({ bookingId: booking._id });
                            setAssignmentsTick(t => t + 1);
                            toast.success('Buchung als gebriefed markiert');
                          } catch (err) {
                            toast.error('Fehler beim Markieren als gebriefed');
                          }
                        }}
                      >
                        Als gebriefed markieren
                      </Button>
                    </Box>
                  </Alert>
                )}
                {showNotPresentForBooking && (
                  <Alert
                    severity='warning'
                    sx={{
                      textTransform: 'none',
                      opacity: 0.9,
                      p: 0.5,
                    }}
                  >
                    Gruppe nicht anwesend
                  </Alert>
                )}
                {showMissingSlotWarning && (
                  <Alert severity='warning' sx={{ textTransform: 'none', opacity: 0.9, p: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant='body2' sx={{ mr: 1 }}>
                        Dieser Buchung ist noch kein Slot zugeordnet
                      </Typography>
                      {selectedSlot === 'green' && (
                        <Button
                          size='small'
                          variant='outlined'
                          color='success'
                          onClick={async e => {
                            e.stopPropagation();
                            try {
                              await updateInternalBooking(
                                booking._id,
                                { ltSpecific: { playerGroupID: 1 } },
                                null,
                                null,
                                null
                              );
                              setAssignmentsTick(t => t + 1);
                            } catch (err) {
                              toast.error('Fehler beim Zuweisen zum grünen Slot');
                            }
                          }}
                        >
                          Dem grünen Slot zuweisen
                        </Button>
                      )}
                      {selectedSlot === 'orange' && (
                        <Button
                          size='small'
                          variant='outlined'
                          color='warning'
                          onClick={async e => {
                            e.stopPropagation();
                            try {
                              await updateInternalBooking(
                                booking._id,
                                { ltSpecific: { playerGroupID: 0 } },
                                null,
                                null,
                                null
                              );
                              setAssignmentsTick(t => t + 1);
                            } catch (err) {
                              toast.error('Fehler beim Zuweisen zum orangenen Slot');
                            }
                          }}
                        >
                          Dem orangenen Slot zuweisen
                        </Button>
                      )}
                      {selectedSlot === 'all' && (
                        <>
                          <Button
                            size='small'
                            variant='outlined'
                            color='success'
                            onClick={async e => {
                              e.stopPropagation();
                              try {
                                await updateInternalBooking(
                                  booking._id,
                                  { ltSpecific: { playerGroupID: 1 } },
                                  null,
                                  null,
                                  null
                                );
                                setAssignmentsTick(t => t + 1);
                              } catch (err) {
                                toast.error('Fehler beim Zuweisen zum grünen Slot');
                              }
                            }}
                          >
                            Dem grünen Slot zuweisen
                          </Button>
                          <Button
                            size='small'
                            variant='outlined'
                            color='warning'
                            onClick={async e => {
                              e.stopPropagation();
                              try {
                                await updateInternalBooking(
                                  booking._id,
                                  { ltSpecific: { playerGroupID: 0 } },
                                  null,
                                  null,
                                  null
                                );
                                setAssignmentsTick(t => t + 1);
                              } catch (err) {
                                toast.error('Fehler beim Zuweisen zum orangenen Slot');
                              }
                            }}
                          >
                            Dem orangenen Slot zuweisen
                          </Button>
                        </>
                      )}
                    </Box>
                  </Alert>
                )}
              </Box>
            )}

          {/* Buttons moved into the missing-slot Alert above */}

          <Collapse in={isExpanded}>
            <Box sx={{ mt: 1 }}>
              <Stack spacing={0.25}>
                {(() => {
                  const lines = [];
                  const totalInBooking = bookedTotalCount;
                  const checkedInPresent = (participants || []).filter(
                    pm => !isPlaceholderMember(pm)
                  ).length;
                  const notAttended = Math.max(0, Number(getPlaceholdersCount(booking)) || 0);
                  const sitOutShow =
                    sitOutDisplayCount != null
                      ? Math.max(0, Number(sitOutDisplayCount) || 0)
                      : Math.max(0, Number(sitOutCount) || 0);
                  const notCheckedIn = Math.max(
                    0,
                    Number(totalInBooking) -
                      Number(notAttended) -
                      Number(sitOutShow) -
                      Number(checkedInPresent)
                  );
                  if (totalInBooking > 0) {
                    lines.push(
                      <Typography key='c-total' variant='body2' color='text.secondary'>
                        {`${totalInBooking} Teilnehmer in Buchung`}
                      </Typography>
                    );
                  }
                  if (onlyShowCheckedIn && notCheckedIn > 0) {
                    lines.push(
                      <Typography key='c-checkedin' variant='body2' color='text.secondary'>
                        {`${notCheckedIn} Teilnehmer nicht eingecheckt`}
                      </Typography>
                    );
                  }
                  if (notAttended > 0) {
                    lines.push(
                      <Typography key='c-notatt' variant='body2' color='text.secondary'>
                        {`${notAttended} Teilnehmer nicht angetreten`}
                      </Typography>
                    );
                  }
                  if (sitOutDisplayCount != null && sitOutShow > 0) {
                    lines.push(
                      <Typography key='c-sitout' variant='body2' color='text.secondary'>
                        {`${
                          sitOutShow > 1
                            ? sitOutShow + ' Teilnehmer setzen aus'
                            : '1 Teilnehmer setzt aus'
                        }`}
                      </Typography>
                    );
                  }
                  return lines.length > 0 ? <Stack spacing={0.25}>{lines}</Stack> : null;
                })()}
              </Stack>
            </Box>
            {onChangeSitOutCount && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  {sitOutCount
                    ? `${
                        Math.max(0, Number(sitOutCount) || 0) > 1
                          ? Math.max(0, Number(sitOutCount) || 0) +
                            ' Teilnehmer setzen in der nächsten Runde aus'
                          : '1 Teilnehmer setzt in der nächsten Runde aus'
                      }`
                    : '0 Teilnehmer setzen in der nächsten Runde aus'}
                </Typography>
                <ButtonGroup size='small' variant='outlined'>
                  <Button
                    disabled={!sitOutCount || Number(sitOutCount) <= 0}
                    onClick={e => {
                      e.stopPropagation();
                      const current = Math.max(0, Number(sitOutCount) || 0);
                      onChangeSitOutCount(Math.max(0, current - 1));
                    }}
                  >
                    -
                  </Button>
                  <Button
                    disabled={(() => {
                      const current = Math.max(0, Number(sitOutCount) || 0);
                      const potential = Math.max(0, getEffectiveParticipantCount(booking));
                      const maxAllowed = Math.max(0, potential - presentCount);
                      return current >= maxAllowed;
                    })()}
                    onClick={e => {
                      e.stopPropagation();
                      const current = Math.max(0, Number(sitOutCount) || 0);
                      const potential = Math.max(0, getEffectiveParticipantCount(booking));
                      const maxAllowed = Math.max(0, potential - presentCount);
                      const next = Math.min(maxAllowed, current + 1);
                      onChangeSitOutCount(next);
                    }}
                  >
                    +
                  </Button>
                  <Tooltip title='Komplette Gruppe aussetzen'>
                    <Button
                      disabled={(() => {
                        const potential = Math.max(0, getEffectiveParticipantCount(booking));
                        const maxAllowed = Math.max(0, potential - presentCount);
                        return maxAllowed <= 0;
                      })()}
                      onClick={e => {
                        e.stopPropagation();
                        const potential = Math.max(0, getEffectiveParticipantCount(booking));
                        const maxAllowed = Math.max(0, potential - presentCount);
                        onChangeSitOutCount(maxAllowed);
                      }}
                      sx={{ minWidth: 36 }}
                    >
                      Alle
                    </Button>
                  </Tooltip>
                </ButtonGroup>
              </Box>
            )}
            <Typography variant='body2' sx={{ fontWeight: 500, mt: 1 }}>
              Spielende Teilnehmer ({checkedInCount}) :{' '}
            </Typography>
            <List dense sx={{ ml: 3 }}>
              {(onlyShowCheckedIn
                ? participants || []
                : booking?.ltSpecific?.participatingMembers || []
              ).map(bpmId => {
                const idStr = typeof bpmId === 'string' ? bpmId : bpmId?._id;
                const meta = typeof bpmId === 'object' ? bpmId : {};
                if (isPlaceholderMember(meta)) return null;
                const isCheckedIn = participants.some(p => String(p._id) === String(idStr));
                const displayName = `${meta.firstName || ''}${
                  meta.firstName && meta.playerName ? ' / ' : ''
                }${meta.playerName || ''}`.trim();
                return (
                  <ListItem
                    key={idStr}
                    disableGutters
                    sx={{ py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    {!hideCheckIcons &&
                      (isCheckedIn ? (
                        <CheckBoxIcon color='success' fontSize='small' />
                      ) : (
                        <CheckBoxOutlineBlankIcon color='disabled' fontSize='small' />
                      ))}
                    <Typography variant='body2' sx={{ fontWeight: 500 }}>
                      {displayName || idStr}
                    </Typography>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>

          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {!isExpanded && onInformedChange && (
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={!!isInformed}
                    onChange={e => {
                      e.stopPropagation();
                      onInformedChange(e.target.checked);
                    }}
                    onClick={e => e.stopPropagation()}
                  />
                }
                label='Informiert'
                onClick={e => e.stopPropagation()}
                sx={{ m: 0, alignSelf: 'flex-start' }}
              />
            )}
            <Button
              variant='text'
              size='small'
              fullWidth
              onClick={toggleExpanded}
              endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
              sx={{ justifyContent: 'flex-start', px: 1 }}
            >
              {isExpanded ? 'Teilnehmer ausblenden' : 'Teilnehmer anzeigen'}
            </Button>
          </Box>
        </Paper>
      );
    }
  );

  const SortableBooking = memo(
    ({
      booking,
      id,
      onUnassign,
      participants = [],
      slotId,
      children,
      draggable = true,
      canSplit = false,
      onSplit = null,
      isSplit = false,
      onMerge = null,
      teamNumber = null,
      splitCount = null,
      onChangeSplitCount = null,
      onOpenDetails = null,
      sitOutCount = 0,
      onChangeSitOutCount = null,
      isInformed = false,
      onInformedChange = null,
    }) => {
      const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
      });
      const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 1,
        position: 'relative',
        // pan-y keeps native vertical scroll; drag still activates after TouchSensor delay + movement
        touchAction: 'pan-y',
        cursor: draggable ? (isDragging ? 'grabbing' : 'grab') : 'default',
      };

      return (
        <div
          ref={setNodeRef}
          style={style}
          {...(draggable ? attributes : {})}
          {...(draggable ? listeners : {})}
        >
          <BookingCard
            booking={booking}
            onUnassign={onUnassign}
            participants={participants}
            slotId={slotId}
            canSplit={canSplit}
            onSplit={onSplit}
            isSplit={isSplit}
            onMerge={onMerge}
            teamNumber={teamNumber}
            splitCount={splitCount}
            onChangeSplitCount={onChangeSplitCount}
            onOpenDetails={onOpenDetails}
            sitOutCount={sitOutCounts[String(booking._id)] || 0}
            onChangeSitOutCount={onChangeSitOutCount}
            isInformed={isInformed}
            onInformedChange={onInformedChange}
          />
          {children}
        </div>
      );
    }
  );

  const TeamContainer = ({
    team,
    bookings,
    participantsByBooking,
    slotId,
    droppableRef = null,
    isOver = false,
  }) => {
    const teamBorder = team.borderColor || team.color;
    // Allocates checked-in participants of a split booking across its teams proportionally
    const getAllocatedCheckedInForTeam = (bookingId, splitMap, teamNumber, totalCheckedIn) => {
      try {
        if (!splitMap || typeof splitMap !== 'object') {
          return Math.max(0, Number(totalCheckedIn) || 0);
        }
        const entries = Object.entries(splitMap)
          .map(([k, v]) => ({ team: Number(k), capacity: Math.max(0, Number(v) || 0) }))
          .filter(e => !Number.isNaN(e.team) && e.capacity > 0)
          .sort((a, b) => a.team - b.team);
        const totalCap = entries.reduce((s, e) => s + e.capacity, 0);
        let remaining = Math.max(0, Number(totalCheckedIn) || 0);
        if (totalCap <= 0 || remaining <= 0) return 0;

        // Initial proportional allocation using floor
        const allocations = entries.map(e => {
          const raw = (remaining * e.capacity) / totalCap;
          const base = Math.min(e.capacity, Math.floor(raw));
          return { ...e, base, frac: raw - Math.floor(raw) };
        });
        let allocatedSum = allocations.reduce((s, a) => s + a.base, 0);
        let leftover = Math.max(0, remaining - allocatedSum);

        // Distribute leftover by largest fractional part while respecting capacity
        while (leftover > 0) {
          // Pick candidate with highest frac and remaining capacity
          let bestIdx = -1;
          let bestFrac = -1;
          for (let i = 0; i < allocations.length; i++) {
            const a = allocations[i];
            if (a.base < a.capacity && a.frac > bestFrac) {
              bestFrac = a.frac;
              bestIdx = i;
            }
          }
          if (bestIdx === -1) break;
          allocations[bestIdx].base += 1;
          allocations[bestIdx].frac = 0;
          leftover -= 1;
        }

        const target = allocations.find(a => a.team === Number(teamNumber));
        return target ? Math.max(0, Math.min(target.base, target.capacity)) : 0;
      } catch (_) {
        return Math.max(0, Number(totalCheckedIn) || 0);
      }
    };
    // Calculate team check-in status
    const teamCheckInStatus = useMemo(() => {
      let totalParticipants = 0;
      let checkedInParticipants = 0;

      for (const booking of bookings) {
        const bid = String(booking._id);
        const sq = splitQuantities[bid]?.[team.number];
        const baseEffective = getAdjustedEffectiveParticipantCount(booking);
        const bookingTotalCount = sq != null ? Number(sq) : baseEffective;

        totalParticipants += bookingTotalCount;
        // Checked-in participants across the entire booking (only those allowed to play)
        const checkedInForBookingTotal = (participantsByBooking.get(bid) || []).filter(
          pm => !isPlaceholderMember(pm)
        ).length;
        const availableCheckedIn = Math.min(checkedInForBookingTotal, baseEffective);
        // Allocate deterministically across split teams to avoid double counting
        const splitMap = splitQuantities[bid];
        const allocatedToThisTeam = splitMap
          ? getAllocatedCheckedInForTeam(bid, splitMap, team.number, availableCheckedIn)
          : Math.min(availableCheckedIn, bookingTotalCount);
        const teamCheckedIn = Math.min(allocatedToThisTeam, bookingTotalCount);
        checkedInParticipants += teamCheckedIn;
      }

      return {
        totalParticipants,
        checkedInParticipants,
        allCheckedIn: totalParticipants > 0 && checkedInParticipants === totalParticipants,
      };
    }, [bookings, participantsByBooking, team.number, splitQuantities]);

    return (
      <Paper
        ref={droppableRef}
        sx={{
          p: 2,
          width: '100%',
          minHeight: 260,
          borderLeft: `4px solid ${teamBorder}`,
          // Highlight and ensure the droppable area equals the full visual container
          border: isOver ? `3px solid ${teamBorder}` : '2px solid transparent',
          backgroundColor: isOver ? `${team.color}15` : `${team.color}10`,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
        }}
      >
        <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <Typography variant='h6'>Team {team.name}</Typography>
            {teamCheckInStatus.allCheckedIn && (
              <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
            )}
          </Stack>
          <Typography variant='subtitle1' color='text.secondary'>
            {`${teamCheckInStatus.checkedInParticipants}/${teamCheckInStatus.totalParticipants} eingecheckt`}
          </Typography>
        </Stack>
        <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
          <SortableContext
            items={bookings.map(b => `${b._id}::${team.number}`)}
            strategy={verticalListSortingStrategy}
          >
            {bookings.map(booking => {
              const assignmentCount = groupAssignments.filter(
                a => a.bookingId === booking._id
              ).length;
              const isSplit = assignmentCount > 1;
              const totalCount = getAdjustedEffectiveParticipantCount(booking);
              const sc = splitQuantities[String(booking._id)]?.[team.number] ?? totalCount;
              const bid = String(booking._id);
              const checkedInForBooking = (participantsByBooking.get(bid) || []).filter(
                pm => !isPlaceholderMember(pm)
              ).length;
              const splitMap = splitQuantities[bid];
              const allocatedCheckedIn = splitMap
                ? getAllocatedCheckedInForTeam(bid, splitMap, team.number, checkedInForBooking)
                : Math.min(checkedInForBooking, sc);
              return (() => {
                const currentItemId = `${booking._id}::${team.number}`;
                const draggedBookingId =
                  typeof activeId === 'string' && activeId.includes('::')
                    ? activeId.split('::')[0]
                    : String(activeId || '');
                const isActiveSplitOfThisBooking =
                  isSplit && draggedBookingId && String(draggedBookingId) === String(booking._id);
                const shouldHide = isActiveSplitOfThisBooking && String(activeId) !== currentItemId;
                if (shouldHide) return null;

                return (
                  <SortableBooking
                    key={`${booking._id}::${team.number}`}
                    id={`${booking._id}::${team.number}`}
                    booking={booking}
                    onUnassign={isSplit ? null : () => handleUnassign(booking._id)}
                    participants={participantsByBooking.get(String(booking._id)) || []}
                    slotId={slotId}
                    draggable={true}
                    isSplit={isSplit}
                    onMerge={isSplit ? () => handleMergeBooking(booking._id) : null}
                    teamNumber={team.number}
                    splitCount={sc}
                    onChangeSplitCount={
                      isSplit
                        ? next => handleChangeSplitCount(String(booking._id), team.number, next)
                        : null
                    }
                    // Pass allocated checked-in to ensure correct checkmark per split share
                    allocatedCheckedIn={allocatedCheckedIn}
                    onOpenDetails={handleOpenDetails}
                    sitOutCount={sitOutCounts[String(booking._id)] || 0}
                    onChangeSitOutCount={count =>
                      handleChangeSitOutCount(String(booking._id), count)
                    }
                    isInformed={
                      !!(currentPreparingGame?.informedByBooking || {})[String(booking._id)]
                    }
                    onInformedChange={informed =>
                      handleInformedChange(String(booking._id), informed)
                    }
                  />
                );
              })();
            })}
          </SortableContext>
        </Box>
      </Paper>
    );
  };

  const TeamDropZone = ({ team, bookings, participantsByBooking, slotId, globalOverId = null }) => {
    const { setNodeRef, isOver } = useDroppable({
      id: `team-${team.number}`,
      data: {
        type: 'team',
        teamNumber: team.number,
        teamColor: team.color,
      },
    });

    const derivedActive = (() => {
      if (isOver) return true;
      const gid = String(globalOverId || '');
      if (gid === `team-${team.number}`) return true;
      if (gid.includes('::')) {
        const parts = gid.split('::');
        const maybeTeam = Number(parts[1]);
        if (maybeTeam === Number(team.number)) return true;
      }
      return false;
    })();

    return (
      <TeamContainer
        team={team}
        bookings={bookings}
        participantsByBooking={participantsByBooking}
        slotId={slotId}
        droppableRef={setNodeRef}
        isOver={derivedActive}
      />
    );
  };

  const UnassignedDropZone = ({ children, globalOverId = null }) => {
    const { setNodeRef, isOver } = useDroppable({
      id: 'unassigned',
      data: {
        type: 'unassigned',
      },
    });
    const derivedActive = (() => {
      if (isOver) return true;
      const gid = String(globalOverId || '');
      if (gid === 'unassigned') return true;
      if (!gid.startsWith('team-') && !gid.includes('::') && gid) return true;
      return false;
    })();

    return (
      <Box
        ref={setNodeRef}
        sx={{
          border: derivedActive
            ? theme => `3px solid ${theme.palette.grey[500]}`
            : '2px solid transparent',
          borderRadius: 2,
          transition: 'border 0.2s ease',
          minHeight: 120,
          p: 1,
          touchAction: 'pan-y',
          userSelect: 'none',
          backgroundColor: derivedActive
            ? theme => alpha(theme.palette.grey[500], 0.082)
            : 'transparent',
        }}
      >
        {children}
      </Box>
    );
  };

  const handleUnassign = async bookingId => {
    try {
      await deleteGroupAssignment({
        bookingId,
        teamCount,
        slot: getSlotParamForRequests(),
        entertainmentId,
      });
      setGroupAssignments(prev => prev.filter(a => a.bookingId !== bookingId));
      const previousSplits = splitQuantitiesRef.current || {};
      if (previousSplits[String(bookingId)]) {
        const nextSplits = { ...previousSplits };
        delete nextSplits[String(bookingId)];
        setSplitQuantities(nextSplits);
        await persistSplitQuantities(nextSplits, previousSplits);
      }
    } catch (err) {
      toast.error('Fehler beim Aufheben der Teamzuweisung');
    }
  };

  const teamColors = LASERTAG_TEAM_COLORS;

  const hasAnySplit = useMemo(() => {
    const seen = new Set();
    for (const a of groupAssignments) {
      const key = String(a.bookingId);
      if (seen.has(key)) return true;
      seen.add(key);
    }
    return false;
  }, [groupAssignments]);

  const getTeamTotals = useMemo(() => {
    // returns a function to compute current totals per team using latest state
    return () => {
      const totals = new Map();
      for (const a of groupAssignments) {
        const tn = Number(a.teamNumber);
        if (!totals.has(tn)) totals.set(tn, 0);
        const booking = gaBookings.find(b => String(b._id) === String(a.bookingId));
        const count = getAdjustedEffectiveParticipantCount(booking || {});
        totals.set(tn, (totals.get(tn) || 0) + Number(count || 0));
      }
      return totals;
    };
  }, [groupAssignments, gaBookings]);

  const persistAssignments = async (
    prevAssignments,
    nextAssignments,
    targetCount,
    slotParam,
    options = {}
  ) => {
    const { triggerTick = true } = options;
    try {
      const buildMap = arr => {
        const map = new Map();
        for (const a of arr) {
          const bid = String(a.bookingId);
          const tn = Number(a.teamNumber);
          if (!map.has(bid)) map.set(bid, new Set());
          map.get(bid).add(tn);
        }
        return map;
      };
      const prevMap = buildMap(prevAssignments);
      const nextMap = buildMap(nextAssignments);

      const allBookingIds = new Set([...Array.from(prevMap.keys()), ...Array.from(nextMap.keys())]);

      for (const bid of allBookingIds) {
        const prevSet = prevMap.get(bid) || new Set();
        const nextSet = nextMap.get(bid) || new Set();
        const changed = (() => {
          if (prevSet.size !== nextSet.size) return true;
          for (const v of prevSet) if (!nextSet.has(v)) return true;
          return false;
        })();
        if (!changed) continue;

        // Coarse strategy: delete all for booking, then re-add each next assignment
        if (prevSet.size > 0) {
          await deleteGroupAssignment({
            bookingId: bid,
            teamCount: targetCount,
            slot: slotParam,
            entertainmentId,
          });
        }
        const nextTeams = Array.from(nextSet).sort((a, b) => a - b);
        for (let i = 0; i < nextTeams.length; i++) {
          const tn = nextTeams[i];
          await updateGroupAssignment({
            bookingId: bid,
            teamNumber: tn,
            teamCount: targetCount,
            allowDuplicate: i > 0,
            slot: slotParam,
            entertainmentId,
          });
        }
      }

      // Ensure backend teamCount is updated when nothing else changed
      if (allBookingIds.size === 0 && nextAssignments.length > 0) {
        const first = nextAssignments[0];
        await updateGroupAssignment({
          bookingId: String(first.bookingId),
          teamNumber: Number(first.teamNumber),
          teamCount: targetCount,
          slot: slotParam,
          entertainmentId,
        });
      }
      if (triggerTick) {
        setAssignmentsTick(t => t + 1);
      }
    } catch (err) {
      toast.error('Fehler beim Aktualisieren der Teamanzahl');
    }
  };

  const handleAutoAssign = async () => {
    const previousAssignments = groupAssignments;
    const previousSplits = splitQuantitiesRef.current || {};
    try {
      const bookingsForView = combinedDedupedForCurrentSlot;
      const { assignments: nextAssignments, splitQuantitiesMap } = computeAutoAssignments(
        bookingsForView,
        Number(teamCount),
        true,
        getAdjustedEffectiveParticipantCount
      );

      // Compact team assignments and reduce team count if needed
      const {
        assignments: compactedAssignments,
        teamCount: newTeamCount,
        splitQuantitiesMap: compactedSplitQuantities,
      } = compactTeamAssignments(nextAssignments, Number(teamCount), splitQuantitiesMap);

      setGroupAssignments(compactedAssignments);
      const nextSplits = compactedSplitQuantities || {};
      setSplitQuantities(nextSplits);
      await persistAssignments(
        previousAssignments,
        compactedAssignments,
        newTeamCount,
        getSlotParamForRequests(),
        { triggerTick: false }
      );
      await persistSplitQuantities(nextSplits, previousSplits, newTeamCount);
      setTeamAssignmentFull({
        teamCount: newTeamCount,
        assignments: compactedAssignments.map(a => ({
          bookingId: a.bookingId,
          teamNumber: a.teamNumber,
        })),
        splitQuantities: splitStateToArray(nextSplits),
      });
      setTeamCount(newTeamCount);
      setAssignmentsTick(t => t + 1);
    } catch (err) {
      toast.error('Fehler bei der automatischen Teamverteilung');
      setAssignmentsTick(t => t + 1);
      setGroupAssignments(previousAssignments);
      setSplitQuantities(previousSplits);
    }
  };

  const handleAutoAssignNoSplit = async () => {
    try {
      const bookingsForView = combinedDedupedForCurrentSlot;
      const { assignments: nextAssignments } = computeAutoAssignments(
        bookingsForView,
        Number(teamCount),
        false,
        getAdjustedEffectiveParticipantCount
      );

      // Compact team assignments and reduce team count if needed
      const {
        assignments: compactedAssignments,
        teamCount: newTeamCount,
        splitQuantitiesMap: compactedSplitQuantities,
      } = compactTeamAssignments(nextAssignments, Number(teamCount), {});

      const previousAssignments = groupAssignments;
      setGroupAssignments(compactedAssignments);
      setSplitQuantities(compactedSplitQuantities);
      setTeamCount(newTeamCount);

      await persistAssignments(
        previousAssignments,
        compactedAssignments,
        newTeamCount,
        getSlotParamForRequests()
      );
    } catch (err) {
      toast.error('Fehler bei der automatischen Teamverteilung');
      setAssignmentsTick(t => t + 1);
    }
  };

  const handleAutoAssignBestTeamCount = async (kMin = 2, kMax = 7) => {
    const previousAssignments = groupAssignments;
    const previousSplits = splitQuantitiesRef.current || {};
    const previousTeamAssignment = teamAssignmentFull;
    try {
      const bookingsForView = combinedDedupedForCurrentSlot;

      const bookingSizeById = new Map();
      for (const b of bookingsForView) {
        const total = getAdjustedEffectiveParticipantCount(b);
        bookingSizeById.set(String(b._id), total);
      }

      const computeLoads = (k, assignments, splitQuantitiesMap) => {
        const loads = Array.from({ length: k }, () => 0);
        const seenSplit = new Set();
        for (const a of assignments) {
          const bid = String(a.bookingId);
          const tn = Number(a.teamNumber);
          const sq = splitQuantitiesMap?.[bid];
          if (sq && sq[tn] != null) {
            loads[tn - 1] += Number(sq[tn]) || 0;
            seenSplit.add(bid);
          } else {
            // Only add full count if this booking isn't split (avoid double counting)
            if (!seenSplit.has(bid)) {
              loads[tn - 1] += Number(bookingSizeById.get(bid) || 0);
            }
          }
        }
        return loads;
      };

      const avgDeviation = loads => {
        if (!loads.length) return Infinity;
        const sum = loads.reduce((s, v) => s + v, 0);
        const mean = sum / loads.length;
        return loads.reduce((s, v) => s + Math.abs(v - mean), 0) / loads.length;
      };

      let best = null;
      for (let k = kMin; k <= kMax; k++) {
        const { assignments, splitQuantitiesMap } = computeAutoAssignments(
          bookingsForView,
          k,
          true,
          getAdjustedEffectiveParticipantCount
        );
        const loads = computeLoads(k, assignments, splitQuantitiesMap);
        const deviation = avgDeviation(loads);
        const maxDiff = Math.max(...loads) - Math.min(...loads);
        if (
          !best ||
          deviation < best.deviation ||
          (deviation === best.deviation && maxDiff < best.maxDiff)
        ) {
          best = { k, assignments, splitQuantitiesMap, deviation, maxDiff };
        }
      }

      if (!best) {
        toast.error('Keine gültige Teamgröße gefunden');
        return;
      }

      const {
        assignments: compactedAssignments,
        teamCount: newTeamCount,
        splitQuantitiesMap: compactedSplitQuantities,
      } = compactTeamAssignments(best.assignments, Number(best.k), best.splitQuantitiesMap || {});

      setGroupAssignments(compactedAssignments);
      const nextSplits = compactedSplitQuantities || {};
      setSplitQuantities(nextSplits);
      await persistAssignments(
        previousAssignments,
        compactedAssignments,
        Number(newTeamCount),
        getSlotParamForRequests(),
        { triggerTick: false }
      );
      await persistSplitQuantities(nextSplits, previousSplits, newTeamCount);
      setTeamAssignmentFull({
        teamCount: newTeamCount,
        assignments: compactedAssignments.map(a => ({
          bookingId: a.bookingId,
          teamNumber: a.teamNumber,
        })),
        splitQuantities: splitStateToArray(nextSplits),
      });
      setTeamCount(newTeamCount);
      setAssignmentsTick(t => t + 1);
    } catch (err) {
      toast.error('Fehler bei der automatischen Teamverteilung');
      setAssignmentsTick(t => t + 1);
      setGroupAssignments(previousAssignments);
      setSplitQuantities(previousSplits);
      setTeamAssignmentFull(previousTeamAssignment);
    }
  };

  const handleAutoAssignBestTeamCountNoSplit = async (kMin = 2, kMax = 7) => {
    const previousAssignments = groupAssignments;
    const previousSplits = splitQuantitiesRef.current || {};
    const previousTeamAssignment = teamAssignmentFull;
    try {
      const bookingsForView = combinedDedupedForCurrentSlot;

      const bookingSizeById = new Map();
      for (const b of bookingsForView) {
        const total = getAdjustedEffectiveParticipantCount(b);
        bookingSizeById.set(String(b._id), total);
      }

      const computeLoadsNoSplit = (k, assignments) => {
        const loads = Array.from({ length: k }, () => 0);
        for (const a of assignments) {
          const bid = String(a.bookingId);
          const tn = Number(a.teamNumber);
          loads[tn - 1] += Number(bookingSizeById.get(bid) || 0);
        }
        return loads;
      };

      const avgDeviation = loads => {
        if (!loads.length) return Infinity;
        const sum = loads.reduce((s, v) => s + v, 0);
        const mean = sum / loads.length;
        return loads.reduce((s, v) => s + Math.abs(v - mean), 0) / loads.length;
      };

      let best = null;
      for (let k = kMin; k <= kMax; k++) {
        const { assignments } = computeAutoAssignments(
          bookingsForView,
          k,
          false,
          getAdjustedEffectiveParticipantCount
        );
        const loads = computeLoadsNoSplit(k, assignments);
        const deviation = avgDeviation(loads);
        const maxDiff = Math.max(...loads) - Math.min(...loads);
        if (
          !best ||
          deviation < best.deviation ||
          (deviation === best.deviation && maxDiff < best.maxDiff)
        ) {
          best = { k, assignments, deviation, maxDiff };
        }
      }

      if (!best) {
        toast.error('Keine gültige Teamgröße gefunden');
        return;
      }

      const {
        assignments: compactedAssignments,
        teamCount: newTeamCount,
        splitQuantitiesMap: compactedSplitQuantities,
      } = compactTeamAssignments(best.assignments, Number(best.k), {});

      setGroupAssignments(compactedAssignments);
      const nextSplits = compactedSplitQuantities || {};
      setSplitQuantities(nextSplits);
      await persistAssignments(
        previousAssignments,
        compactedAssignments,
        Number(newTeamCount),
        getSlotParamForRequests(),
        { triggerTick: false }
      );
      await persistSplitQuantities(nextSplits, previousSplits, newTeamCount);
      setTeamAssignmentFull({
        teamCount: newTeamCount,
        assignments: compactedAssignments.map(a => ({
          bookingId: a.bookingId,
          teamNumber: a.teamNumber,
        })),
        splitQuantities: splitStateToArray(nextSplits),
      });
      setTeamCount(newTeamCount);
      setAssignmentsTick(t => t + 1);
    } catch (err) {
      toast.error('Fehler bei der automatischen Teamverteilung');
      setAssignmentsTick(t => t + 1);
      setGroupAssignments(previousAssignments);
      setSplitQuantities(previousSplits);
      setTeamAssignmentFull(previousTeamAssignment);
    }
  };

  const handleClearAssignments = async () => {
    try {
      const previousAssignments = groupAssignments;
      setGroupAssignments([]);
      setSplitQuantities({});
      setTeamCount(2); // Reset to default team count when clearing all assignments
      await persistAssignments(previousAssignments, [], 2, getSlotParamForRequests());
    } catch (err) {
      toast.error('Fehler beim Aufheben aller Teamzuweisungen');
      setAssignmentsTick(t => t + 1);
    }
  };

  const handleEndGame = async () => {
    try {
      setIsEndingGame(true);
      const { game } = endConfirmDialog;
      if (!game) return;
      await endGame({ gameId: game._id, entertainmentId });
      setActiveSitOutCounts({});
      setAssignmentsTick(t => t + 1);
      setParticipantsTick(t => t + 1);
      setEndConfirmDialog({ open: false, game: null });
    } catch (e) {
      toast.error('Fehler beim Beenden des Spiels');
    } finally {
      setIsEndingGame(false);
    }
  };

  const handleSplitBooking = async (booking, teamA = 1, teamB = 2) => {
    const previousAssignments = groupAssignments;
    const previousSplits = splitQuantitiesRef.current || {};
    try {
      const bid = String(booking._id);
      const total = getAdjustedEffectiveParticipantCount(booking);

      // Choose a balanced split based on current team totals
      const totals = getTeamTotals();
      const tA = Number(totals.get(teamA) || 0);
      const tB = Number(totals.get(teamB) || 0);
      // We want tA + x and tB + (total - x) as close as possible -> choose x near (tB - tA + total)/2
      let x = Math.round((tB - tA + total) / 2);
      x = Math.max(1, Math.min(total - 1, x));
      const c1 = x;
      const c2 = total - x;
      const next = [
        ...groupAssignments,
        { bookingId: bid, teamNumber: teamA, teamCount },
        { bookingId: bid, teamNumber: teamB, teamCount },
      ];
      setGroupAssignments(next);
      const nextSplits = {
        ...previousSplits,
        [bid]: { [teamA]: c1, [teamB]: c2 },
      };
      setSplitQuantities(nextSplits);
      await updateGroupAssignment({
        bookingId: bid,
        teamNumber: teamA,
        teamCount,
        allowDuplicate: true,
        slot: getSlotParamForRequests(),
        entertainmentId,
      });
      await updateGroupAssignment({
        bookingId: bid,
        teamNumber: teamB,
        teamCount,
        allowDuplicate: true,
        slot: getSlotParamForRequests(),
        entertainmentId,
      });
      await persistSplitQuantities(nextSplits, previousSplits);
      setTeamAssignmentFull({
        teamCount,
        assignments: next.map(a => ({
          bookingId: a.bookingId,
          teamNumber: a.teamNumber,
        })),
        splitQuantities: splitStateToArray(nextSplits),
      });
    } catch (err) {
      toast.error('Fehler beim Aufteilen der Buchung');
      setAssignmentsTick(t => t + 1);
      setGroupAssignments(previousAssignments);
      setSplitQuantities(previousSplits);
    }
  };

  const handleMergeBooking = async bookingId => {
    const previousAssignments = groupAssignments;
    const previousSplits = splitQuantitiesRef.current || {};
    try {
      await deleteGroupAssignment({
        bookingId,
        teamCount,
        slot: getSlotParamForRequests(),
        entertainmentId,
      });
      const nextAssignments = previousAssignments.filter(a => a.bookingId !== bookingId);
      setGroupAssignments(nextAssignments);
      const nextSplits = { ...previousSplits };
      delete nextSplits[String(bookingId)];
      setSplitQuantities(nextSplits);
      await persistSplitQuantities(nextSplits, previousSplits);
      setTeamAssignmentFull({
        teamCount,
        assignments: nextAssignments.map(a => ({
          bookingId: a.bookingId,
          teamNumber: a.teamNumber,
        })),
        splitQuantities: splitStateToArray(nextSplits),
      });
    } catch (err) {
      toast.error('Fehler beim Zusammenführen der Buchung');
      setAssignmentsTick(t => t + 1);
      setGroupAssignments(previousAssignments);
      setSplitQuantities(previousSplits);
    }
  };

  const handleMergeToTeam = async (bookingId, keepTeamNumber) => {
    const previousAssignments = groupAssignments;
    const previousSplits = splitQuantitiesRef.current || {};
    try {
      await deleteGroupAssignment({
        bookingId,
        teamCount,
        slot: getSlotParamForRequests(),
        entertainmentId,
      });
      await updateGroupAssignment({
        bookingId,
        teamNumber: keepTeamNumber,
        teamCount,
        slot: getSlotParamForRequests(),
        entertainmentId,
      });
      const nextAssignments = previousAssignments
        .filter(a => a.bookingId !== bookingId)
        .concat([{ bookingId, teamNumber: keepTeamNumber, teamCount }]);
      setGroupAssignments(nextAssignments);
      const nextSplits = { ...previousSplits };
      delete nextSplits[String(bookingId)];
      setSplitQuantities(nextSplits);
      await persistSplitQuantities(nextSplits, previousSplits);
      setTeamAssignmentFull({
        teamCount,
        assignments: nextAssignments.map(a => ({
          bookingId: a.bookingId,
          teamNumber: a.teamNumber,
        })),
        splitQuantities: splitStateToArray(nextSplits),
      });
    } catch (err) {
      toast.error('Fehler beim Zusammenführen der Buchung');
      setAssignmentsTick(t => t + 1);
      setGroupAssignments(previousAssignments);
      setSplitQuantities(previousSplits);
    }
  };

  const handleChangeSitOutCount = (bookingId, nextCount) => {
    try {
      const bid = String(bookingId);
      const bounded = Math.max(0, Number(nextCount) || 0);
      setSitOutCounts(prev => ({ ...prev, [bid]: bounded }));
      // Persist immediately to backend so counts survive reloads
      try {
        updateSitOutForPreparing({ bookingId: bid, count: bounded, entertainmentId });
      } catch (_) {}
    } catch (_) {}
  };

  const handleInformedChange = async (bookingId, informed) => {
    const bid = String(bookingId);
    try {
      setGames(prev => {
        const next = [...prev];
        const idx = next.findIndex(g => g.status === 'Preparing');
        if (idx < 0) return prev;
        const g = { ...next[idx] };
        g.informedByBooking = { ...(g.informedByBooking || {}), [bid]: !!informed };
        next[idx] = g;
        return next;
      });
      await updateInformedForPreparing({ bookingId: bid, informed: !!informed, entertainmentId });
    } catch (err) {
      toast.error('Fehler beim Speichern des Informiert-Status');
      setParticipantsTick(t => t + 1);
    }
  };

  const handleChangeSplitCount = async (bookingId, teamNumber, nextCount) => {
    const booking = gaBookings.find(b => String(b._id) === String(bookingId));
    if (!booking) return;
    const total = getAdjustedEffectiveParticipantCount(booking);
    const bounded = Math.max(1, Math.min(total - 1, Number(nextCount) || 0));
    const teamsForBooking = Array.from(
      new Set(
        groupAssignments
          .filter(a => String(a.bookingId) === String(bookingId))
          .map(a => Number(a.teamNumber))
      )
    );
    const otherTeam =
      teamsForBooking.find(t => Number(t) !== Number(teamNumber)) ?? (teamNumber === 1 ? 2 : 1);
    const otherCount = Math.max(1, total - bounded);
    const previousSplits = splitQuantitiesRef.current || {};
    const nextSplits = {
      ...previousSplits,
      [String(bookingId)]: {
        ...(previousSplits[String(bookingId)] || {}),
        [Number(teamNumber)]: bounded,
        [Number(otherTeam)]: otherCount,
      },
    };
    setSplitQuantities(nextSplits);
    try {
      await persistSplitQuantities(nextSplits, previousSplits);
      setTeamAssignmentFull(prev => ({
        ...(prev && typeof prev === 'object' ? prev : {}),
        teamCount,
        assignments: groupAssignments.map(a => ({
          bookingId: a.bookingId,
          teamNumber: a.teamNumber,
        })),
        splitQuantities: splitStateToArray(nextSplits),
      }));
    } catch (_) {
      // persistSplitQuantities already reverted state and showed a toast
    }
  };

  const handleTeamCountChange = newCount => {
    setTeamCount(prevCount => {
      const oldCount = Number(prevCount);
      const target = Number(newCount);
      if (oldCount === target) return prevCount;

      // Work on a mutable copy
      let working = groupAssignments.map(a => ({ ...a, teamNumber: Number(a.teamNumber) }));
      let currentCount = oldCount;

      const buildPerTeam = (assignments, count) => {
        const map = new Map();
        for (let i = 1; i <= count; i++) map.set(i, []);
        for (const a of assignments) {
          const t = Number(a.teamNumber);
          if (!map.has(t)) map.set(t, []);
          map.get(t).push(a.bookingId);
        }
        return map;
      };

      if (target < oldCount) {
        // Iteratively reduce team count while preserving filled teams
        while (currentCount > target) {
          const perTeam = buildPerTeam(working, currentCount);
          // First empty slot within the remaining range [1..target]
          let removedIdx = null;
          for (let i = 1; i <= target; i++) {
            if ((perTeam.get(i) || []).length === 0) {
              removedIdx = i;
              break;
            }
          }
          // Check if there is any filled team beyond target
          const hasFilledBeyond = (() => {
            for (let i = target + 1; i <= currentCount; i++) {
              if ((perTeam.get(i) || []).length > 0) return true;
            }
            return false;
          })();

          if (removedIdx != null && hasFilledBeyond) {
            // Remove the empty index and shift all higher teams down by 1
            working = working.map(a => ({
              ...a,
              teamNumber: a.teamNumber > removedIdx ? a.teamNumber - 1 : a.teamNumber,
            }));
            currentCount -= 1;
          } else {
            // Drop the last team index entirely
            working = working.filter(a => a.teamNumber !== currentCount);
            currentCount -= 1;
          }
        }
      } else if (target > oldCount) {
        // Expanding: keep assignments, just increase teamCount
      }

      const nextAssignments = working.map(a => ({ ...a, teamCount: target }));
      setGroupAssignments(nextAssignments);
      const previousSplits = splitQuantitiesRef.current || {};
      const sanitizedSplits = sanitizeSplitStateForAssignments(previousSplits, nextAssignments);
      if (!areSplitStatesEqual(sanitizedSplits, previousSplits)) {
        setSplitQuantities(sanitizedSplits);
        persistSplitQuantities(sanitizedSplits, previousSplits).catch(() => {});
      }
      setTeamAssignmentFull({
        teamCount: target,
        assignments: nextAssignments.map(a => ({
          bookingId: a.bookingId,
          teamNumber: a.teamNumber,
        })),
        splitQuantities: splitStateToArray(sanitizedSplits),
      });

      // Persist changes to backend
      persistAssignments(groupAssignments, nextAssignments, target);

      return target;
    });
  };

  const handleDragStart = event => {
    // Invalidate any in-flight fetch responses
    dragEpochRef.current += 1;
    // Pause auto-refresh during drag
    stopParticipantsTimer();
    stopAssignmentsTimer();
    // Set global cursor to grabbing
    document.body.style.cursor = 'grabbing';
    setActiveId(event.active.id);
    setOverId(null);
  };

  const handleDragEnd = async event => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over) {
      // Reset cursor and resume auto-refresh
      document.body.style.cursor = '';
      startParticipantsTimer();
      startAssignmentsTimer();
      return;
    }

    const bookingId = String(active.id).includes('::')
      ? String(active.id).split('::')[0]
      : active.id;
    let targetId = over.id;
    const overData = over.data.current;

    // Use drop zone data if available for more reliable detection
    if (overData?.type === 'team') {
      targetId = `team-${overData.teamNumber}`;
    } else if (overData?.type === 'unassigned') {
      targetId = 'unassigned';
    } else if (!String(targetId).startsWith('team-') && String(targetId) !== 'unassigned') {
      // If dropped on an item inside a container, resolve to that item's container
      const overIdStr = String(targetId);
      // Support composite ids like bookingId::teamNumber
      if (overIdStr.includes('::')) {
        const [, tnStr] = overIdStr.split('::');
        const tn = parseInt(tnStr, 10);
        if (!Number.isNaN(tn)) {
          targetId = `team-${tn}`;
        } else {
          targetId = 'unassigned';
        }
      } else {
        const overBookingId = overIdStr;
        const overAssignment = groupAssignments.find(a => a.bookingId === overBookingId);
        if (overAssignment) {
          targetId = `team-${overAssignment.teamNumber}`;
        } else {
          targetId = 'unassigned';
        }
      }
    }

    if (String(targetId).startsWith('team-')) {
      const newTeamNumber = parseInt(String(targetId).split('-')[1], 10);
      const existingAssignment = groupAssignments.find(a => a.bookingId === bookingId);
      const splitAssignments = groupAssignments.filter(a => a.bookingId === bookingId);
      const isSplit = splitAssignments.length > 1;
      const initialTeamNumber = String(active.id).includes('::')
        ? parseInt(String(active.id).split('::')[1], 10)
        : null;

      if (!Number.isFinite(newTeamNumber) || newTeamNumber < 1 || newTeamNumber > teamCount) {
        toast.error(`Teamnummer muss zwischen 1 und ${teamCount} liegen`);
        // Reset cursor and resume auto-refresh
        document.body.style.cursor = '';
        startParticipantsTimer();
        startAssignmentsTimer();
        return;
      }

      // When dragging a split booking:
      // - If dropped back onto the same team it started from, do nothing (keep split)
      // - Otherwise, merge the booking and assign fully to the target team
      if (isSplit) {
        if (
          Number.isFinite(initialTeamNumber) &&
          Number(initialTeamNumber) === Number(newTeamNumber)
        ) {
          // No-op: keep current split assignment
          document.body.style.cursor = '';
          startParticipantsTimer();
          startAssignmentsTimer();
          return;
        }
        try {
          await handleMergeToTeam(bookingId, newTeamNumber);
        } catch (err) {
          toast.error('Fehler beim Zusammenführen und Zuweisen');
          setAssignmentsTick(t => t + 1);
        }
        // Reset cursor and resume auto-refresh
        document.body.style.cursor = '';
        startParticipantsTimer();
        startAssignmentsTimer();
        return;
      }

      if (existingAssignment?.teamNumber === newTeamNumber) {
        // No-op move within the same team for non-split booking
        // Reset cursor and resume auto-refresh
        document.body.style.cursor = '';
        startParticipantsTimer();
        startAssignmentsTimer();
        return;
      }
      const previousAssignments = groupAssignments;
      try {
        const nextAssignments = (() => {
          const assignmentExists = previousAssignments.some(a => a.bookingId === bookingId);
          if (assignmentExists) {
            return previousAssignments.map(a =>
              a.bookingId === bookingId ? { ...a, teamNumber: newTeamNumber } : a
            );
          }
          return [...previousAssignments, { bookingId, teamNumber: newTeamNumber, teamCount }];
        })();
        setGroupAssignments(nextAssignments);
        await updateGroupAssignment({
          bookingId,
          teamNumber: newTeamNumber,
          teamCount,
          slot: getSlotParamForRequests(),
          entertainmentId,
        });
      } catch (err) {
        setGroupAssignments(previousAssignments);
        toast.error('Fehler beim Aktualisieren der Teamzuweisung');
      }
    } else if (String(targetId) === 'unassigned') {
      const previousAssignments = groupAssignments;
      const nextAssignments = previousAssignments.filter(a => a.bookingId !== bookingId);
      setGroupAssignments(nextAssignments);
      setSplitQuantities(prev => {
        const copy = { ...prev };
        delete copy[String(bookingId)];
        return copy;
      });
      try {
        await deleteGroupAssignment({
          bookingId,
          teamCount,
          slot: getSlotParamForRequests(),
          entertainmentId,
        });
      } catch (err) {
        setGroupAssignments(previousAssignments);
        toast.error(err.message || 'Fehler beim Aufheben der Teamzuweisung');
      }
    }
    // Reset cursor and resume auto-refresh
    document.body.style.cursor = '';
    startParticipantsTimer();
    startAssignmentsTimer();
  };

  const handleDragCancel = event => {
    setActiveId(null);
    setOverId(null);
    // Reset cursor and resume auto-refresh
    document.body.style.cursor = '';
    startParticipantsTimer();
    startAssignmentsTimer();
  };

  const handleDragOver = event => {
    const { over } = event || {};
    setOverId(over?.id ?? null);
  };

  const activeBooking = activeId
    ? gaBookings.find(
        b =>
          String(b._id) ===
          (String(activeId).includes('::') ? String(activeId).split('::')[0] : String(activeId))
      )
    : null;

  // Map member id -> booking for display purposes
  const memberIdToBooking = useMemo(() => {
    const map = new Map();
    const seen = new Set();
    const all = [...gaBookings];
    for (const b of all) {
      const bid = String(b?._id || b?.id || '');
      if (!bid || seen.has(bid)) continue;
      seen.add(bid);
      const pms = (b.ltSpecific?.participatingMembers || [])
        .map(m => (typeof m === 'string' ? m : m?._id))
        .filter(Boolean);
      for (const id of pms) map.set(String(id), b);
    }
    return map;
  }, [gaBookings]);

  const combinedDedupedForCurrentSlot = useMemo(() => {
    const combined = [];
    const addIfMatch = b => {
      if (!b) return;
      const pid = b?.ltSpecific?.playerGroupID;
      // Filter by selected slot when not viewing 'all'
      if (selectedSlotId != null && pid != null && pid !== selectedSlotId) return;
      combined.push(b);
    };
    gaBookings.forEach(addIfMatch);
    const seen = new Set();
    const deduped = [];
    for (const b of combined) {
      const id = String(b?._id || b?.id || '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      deduped.push(b);
    }
    return deduped;
  }, [gaBookings, selectedSlotId]);

  // Group participating members of the current preparing game by their booking
  const participantsByBookingForCurrentPreparing = useMemo(() => {
    const map = new Map();
    if (!currentPreparingGame) return map;
    for (const m of currentPreparingGame.participatingMembers || []) {
      const memberId = typeof m === 'string' ? m : m?._id;
      if (!memberId) continue;
      const booking = memberIdToBooking.get(String(memberId));
      if (!booking) continue;
      const bid = String(booking._id);
      if (!map.has(bid)) map.set(bid, []);
      map.get(bid).push(m);
    }
    return map;
  }, [currentPreparingGame, memberIdToBooking]);

  const renderNextPreparingCard = (game, slotId) => {
    const usePrimary = selectedSlot === 'all';
    const headerSlotId = usePrimary ? slotId : selectedSlotId;
    const color = usePrimary ? theme.palette.primary.main : getSlotColorById(headerSlotId, theme);
    const effectiveSlotId = usePrimary ? null : selectedSlotId;
    return (
      <Card variant='outlined' sx={{ borderColor: color, height: '100%' }}>
        <CardHeader
          title={
            usePrimary
              ? 'Nächstes Spiel (vorbereiten) – Alle Slots'
              : `Nächstes Spiel (vorbereiten) – ${getSlotLabelById(headerSlotId)}`
          }
          sx={{ color }}
          subheader={(() => {
            try {
              // Build visible list of bookings (same filtering as content area previously)
              const combined = [];
              const addIfPresent = b => {
                if (b) combined.push(b);
              };
              gaBookings.forEach(addIfPresent);
              const deduped = [];
              const seen = new Set();
              for (const b of combined) {
                const id = String(b?._id || b?.id || '');
                if (!id || seen.has(id)) continue;
                seen.add(id);
                deduped.push(b);
              }
              const listForView =
                selectedSlot === 'all' || effectiveSlotId == null
                  ? deduped
                  : deduped.filter(
                      b =>
                        b?.ltSpecific?.playerGroupID === effectiveSlotId ||
                        b?.ltSpecific?.playerGroupID == null
                    );
              let totalAllowed = 0;
              let totalCheckedIn = 0;
              for (const b of listForView) {
                const bid = String(b._id);
                const baseEffective = getAdjustedEffectiveParticipantCount(b);
                totalAllowed += baseEffective;
                const checkedInForBooking = (
                  participantsByBookingForCurrentPreparing.get(bid) || []
                ).filter(pm => !isPlaceholderMember(pm)).length;
                totalCheckedIn += Math.min(checkedInForBooking, baseEffective);
              }
              const allChecked = totalAllowed > 0 && totalCheckedIn === totalAllowed;

              // Detect pending check-ins from bookings assigned to a different slot than currently prepared
              const wrongSlotGroups = (() => {
                try {
                  if (selectedSlot === 'all') return [];
                  const sid = selectedSlotId;
                  const names = new Set();
                  // 1) Already checked-in participants mapped to bookings of other slot
                  try {
                    if (
                      participantsByBookingForCurrentPreparing &&
                      typeof participantsByBookingForCurrentPreparing.entries === 'function'
                    ) {
                      for (const [bid] of participantsByBookingForCurrentPreparing.entries()) {
                        const booking = gaBookings.find(b => String(b._id) === String(bid));
                        if (!booking) continue;
                        const bidSlot = booking?.ltSpecific?.playerGroupID;
                        if (bidSlot == null) continue;
                        if (Number(bidSlot) !== Number(sid)) {
                          const name =
                            `${booking.firstName || ''} ${booking.lastName || ''}`.trim() ||
                            String(booking._id);
                          names.add(name);
                        }
                      }
                    }
                  } catch (_) {}
                  // 2) Pending attempts from other slot
                  try {
                    const pendings = Array.isArray(game?.pendingCheckins)
                      ? game.pendingCheckins.filter(p => p && p.status === 'pending')
                      : [];
                    for (const p of pendings) {
                      const booking = memberIdToBooking.get(String(p.memberId));
                      if (!booking) continue;
                      const bidSlot = booking?.ltSpecific?.playerGroupID;
                      if (bidSlot == null) continue;
                      if (Number(bidSlot) !== Number(sid)) {
                        const name =
                          `${booking.firstName || ''} ${booking.lastName || ''}`.trim() ||
                          String(booking._id);
                        names.add(name);
                      }
                    }
                  } catch (_) {}
                  return Array.from(names);
                } catch (_) {
                  return [];
                }
              })();
              return (
                <Box sx={{ display: 'grid', gap: 0.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant='body2' color='text.secondary'>
                      Eingecheckte Teilnehmer: {totalCheckedIn}/{totalAllowed}
                    </Typography>
                    {allChecked && <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />}
                  </Box>
                  {wrongSlotGroups.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1,
                        py: 0.5,
                        mt: 0.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: `${theme.palette.error.main}33`,
                        backgroundColor: `${theme.palette.error.main}10`,
                        color: 'error.main',
                        width: 'fit-content',
                        maxWidth: '100%',
                      }}
                    >
                      <WarningAmberIcon sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />
                      <Typography variant='body2' color='error' sx={{ m: 0 }}>
                        {`Spieler der Gruppe${
                          wrongSlotGroups.length > 1 ? 'n' : ''
                        } ${wrongSlotGroups.join(
                          ', '
                        )} versuchen sich im falschen Slot einzuloggen`}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            } catch (_) {
              return null;
            }
          })()}
          action={(() => {
            const totalCheckedIn = (() => {
              try {
                const list = Array.isArray(game?.participatingMembers)
                  ? game.participatingMembers
                  : [];
                return list.filter(pm => !isPlaceholderMember(pm)).length;
              } catch (_) {
                return 0;
              }
            })();
            const nonEmptyTeams = (() => {
              try {
                const used = new Set();
                for (const a of groupAssignments) {
                  const tn = Number(a.teamNumber);
                  if (tn >= 1 && tn <= Number(teamCount)) used.add(tn);
                }
                return used.size;
              } catch (_) {
                return 0;
              }
            })();
            const disabledStart =
              !game ||
              isStartingGame ||
              !!activeGame ||
              totalCheckedIn < 2 ||
              nonEmptyTeams < 2 ||
              !canControlGames;
            return (
              <Stack direction='row' spacing={1} marginRight={'9px'}>
                {canControlGames && (
                  <Button
                    variant='contained'
                    color='primary'
                    disabled={disabledStart}
                    onClick={() => handleRequestStart(game._id)}
                    size='small'
                    sx={{
                      minWidth: { xs: 'auto', sm: '120px' },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      px: { xs: 1, sm: 2 },
                    }}
                  >
                    Spiel starten
                  </Button>
                )}
              </Stack>
            );
          })()}
        />
        <Divider />
        <CardContent>
          <>
            {/* moved checked-in indicator into CardHeader subheader */}
            {game &&
              Array.isArray(game.pendingCheckins) &&
              (() => {
                const pendingsAll = game.pendingCheckins.filter(p => p.status === 'pending');
                if (selectedSlot === 'all') return pendingsAll.length > 0;
                const sid = selectedSlotId;
                const filtered = pendingsAll.filter(p => {
                  const booking = memberIdToBooking.get(String(p.memberId));
                  if (!booking) return false;
                  const pid = booking?.ltSpecific?.playerGroupID;
                  return pid == null || Number(pid) === Number(sid);
                });
                return filtered.length > 0;
              })() && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
                    Ausstehende Anmeldeversuche
                  </Typography>
                  {(() => {
                    const pendings = game.pendingCheckins.filter(p => p.status === 'pending');
                    const byBooking = new Map();
                    const unknown = [];
                    for (const p of pendings) {
                      const booking = memberIdToBooking.get(String(p.memberId));
                      if (booking) {
                        // Filter by selected slot if not viewing 'all'
                        if (
                          selectedSlot !== 'all' &&
                          booking?.ltSpecific?.playerGroupID != null &&
                          Number(booking?.ltSpecific?.playerGroupID) !== Number(selectedSlotId)
                        ) {
                          continue;
                        }
                        const bid = String(booking._id);
                        if (!byBooking.has(bid)) byBooking.set(bid, { booking, list: [] });
                        byBooking.get(bid).list.push(p);
                      } else {
                        // Only show unknown pendings when viewing all slots
                        if (selectedSlot === 'all') {
                          unknown.push(p);
                        }
                      }
                    }
                    let groups = Array.from(byBooking.values());
                    // Auto-hide and cleanup pendings that are now inside their booking window
                    groups = groups.filter(({ booking, list }) => {
                      if (!isInsideBookingWindow(booking)) return true;
                      // Booking window is valid now; remove any pending entries for its members
                      Promise.all(
                        (list || []).map(p =>
                          confirmPendingCheckin({ memberId: p.memberId, entertainmentId })
                        )
                      ).catch(() => {});
                      return false;
                    });
                    return (
                      <Stack spacing={2}>
                        {groups.map(({ booking, list }) => (
                          <Paper
                            key={`pending-${booking._id}`}
                            sx={{
                              p: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                            }}
                          >
                            <Stack
                              direction='row'
                              alignItems='center'
                              justifyContent='space-between'
                              sx={{ mb: 1 }}
                            >
                              <Stack
                                direction='row'
                                alignItems='center'
                                spacing={1}
                                flexWrap='wrap'
                              >
                                <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                                  {booking.firstName} {booking.lastName} – {list.length} ausstehend
                                </Typography>
                                {booking.ltSpecific?.groupName && (
                                  <Typography
                                    variant='body1'
                                    sx={{
                                      fontStyle: 'italic',
                                      fontWeight: 500,
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      backgroundColor: 'action.hover',
                                    }}
                                  >
                                    „<GroupNameDisplay name={booking.ltSpecific.groupName} />“
                                  </Typography>
                                )}
                              </Stack>
                              {(() => {
                                const hint = getTimingInfoForBooking(booking);
                                return hint ? (
                                  <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    sx={{ mr: 1 }}
                                  >
                                    {hint}
                                  </Typography>
                                ) : null;
                              })()}
                              <Stack direction='row' spacing={1}>
                                <Button
                                  size='small'
                                  variant='outlined'
                                  color='primary'
                                  onClick={async () => {
                                    try {
                                      // Persist group-level approval for extraordinary check-ins
                                      if (booking && booking._id) {
                                        try {
                                          await updateGroupApproval({
                                            approve: true,
                                            bookingIds: [String(booking._id)],
                                            entertainmentId,
                                          });
                                        } catch (_) {}
                                      }
                                      await Promise.all(
                                        list.map(x =>
                                          confirmPendingCheckin({
                                            memberId: x.memberId,
                                            entertainmentId,
                                          })
                                        )
                                      );
                                      setParticipantsTick(t => t + 1);
                                      setAssignmentsTick(t => t + 1);
                                    } catch (e) {
                                      toast.error('Fehler beim Bestätigen der Gruppe');
                                    }
                                  }}
                                >
                                  Gesamte Gruppe zulassen
                                </Button>
                                <Button
                                  size='small'
                                  variant='outlined'
                                  color='error'
                                  onClick={async () => {
                                    try {
                                      await Promise.all(
                                        list.map(x =>
                                          declinePendingCheckin({
                                            memberId: x.memberId,
                                            entertainmentId,
                                          })
                                        )
                                      );
                                      setParticipantsTick(t => t + 1);
                                      setAssignmentsTick(t => t + 1);
                                    } catch (e) {
                                      toast.error('Fehler beim Ablehnen der Gruppe');
                                    }
                                  }}
                                >
                                  Gesamte Gruppe ablehnen
                                </Button>
                              </Stack>
                            </Stack>
                            <Stack spacing={1}>
                              {list.map(p => (
                                <Stack
                                  key={p.memberId}
                                  direction='row'
                                  alignItems='center'
                                  justifyContent='space-between'
                                  sx={{
                                    p: 1,
                                    border: '1px dashed',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                  }}
                                >
                                  <Typography variant='body2'>
                                    {(p.firstName || '') +
                                      (p.firstName && p.playerName ? ' / ' : '') +
                                      (p.playerName || p.memberId)}
                                  </Typography>
                                  <Stack direction='row' spacing={1}>
                                    <Button
                                      size='small'
                                      variant='outlined'
                                      onClick={async () => {
                                        try {
                                          await confirmPendingCheckin({
                                            memberId: p.memberId,
                                            entertainmentId,
                                          });
                                          setParticipantsTick(t => t + 1);
                                          setAssignmentsTick(t => t + 1);
                                        } catch (e) {
                                          toast.error('Fehler beim Bestätigen');
                                        }
                                      }}
                                    >
                                      Bestätigen
                                    </Button>
                                    <Button
                                      size='small'
                                      variant='outlined'
                                      color='error'
                                      onClick={async () => {
                                        try {
                                          await declinePendingCheckin({
                                            memberId: p.memberId,
                                            entertainmentId,
                                          });
                                          setParticipantsTick(t => t + 1);
                                          setAssignmentsTick(t => t + 1);
                                        } catch (e) {
                                          toast.error('Fehler beim Ablehnen');
                                        }
                                      }}
                                    >
                                      Ablehnen
                                    </Button>
                                  </Stack>
                                </Stack>
                              ))}
                            </Stack>
                          </Paper>
                        ))}
                        {selectedSlot === 'all' && unknown.length > 0 && (
                          <Paper
                            key='pending-unknown'
                            sx={{
                              p: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                            }}
                          >
                            <Stack
                              direction='row'
                              alignItems='center'
                              justifyContent='space-between'
                              sx={{ mb: 1 }}
                            >
                              <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                                Ohne zugeordnete Buchung
                              </Typography>
                              <Stack direction='row' spacing={1}>
                                <Button
                                  size='small'
                                  variant='outlined'
                                  color='primary'
                                  onClick={async () => {
                                    try {
                                      await Promise.all(
                                        unknown.map(x =>
                                          confirmPendingCheckin({
                                            memberId: x.memberId,
                                            entertainmentId,
                                          })
                                        )
                                      );
                                      setParticipantsTick(t => t + 1);
                                      setAssignmentsTick(t => t + 1);
                                    } catch (e) {
                                      toast.error('Fehler beim Bestätigen der Gruppe');
                                    }
                                  }}
                                >
                                  Gesamte Gruppe zulassen
                                </Button>
                                <Button
                                  size='small'
                                  variant='outlined'
                                  color='error'
                                  onClick={async () => {
                                    try {
                                      await Promise.all(
                                        unknown.map(x =>
                                          declinePendingCheckin({
                                            memberId: x.memberId,
                                            entertainmentId,
                                          })
                                        )
                                      );
                                      setParticipantsTick(t => t + 1);
                                      setAssignmentsTick(t => t + 1);
                                    } catch (e) {
                                      toast.error('Fehler beim Ablehnen der Gruppe');
                                    }
                                  }}
                                >
                                  Gesamte Gruppe ablehnen
                                </Button>
                              </Stack>
                            </Stack>
                            <Stack spacing={1}>
                              {unknown.map(p => (
                                <Stack
                                  key={p.memberId}
                                  direction='row'
                                  alignItems='center'
                                  justifyContent='space-between'
                                  sx={{
                                    p: 1,
                                    border: '1px dashed',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                  }}
                                >
                                  <Typography variant='body2'>
                                    {(p.firstName || '') +
                                      (p.firstName && p.playerName ? ' / ' : '') +
                                      (p.playerName || p.memberId)}
                                  </Typography>
                                  <Stack direction='row' spacing={1}>
                                    <Button
                                      size='small'
                                      variant='outlined'
                                      onClick={async () => {
                                        try {
                                          await confirmPendingCheckin({
                                            memberId: p.memberId,
                                            entertainmentId,
                                          });
                                          setParticipantsTick(t => t + 1);
                                          setAssignmentsTick(t => t + 1);
                                        } catch (e) {
                                          toast.error('Fehler beim Bestätigen');
                                        }
                                      }}
                                    >
                                      Bestätigen
                                    </Button>
                                    <Button
                                      size='small'
                                      variant='outlined'
                                      color='error'
                                      onClick={async () => {
                                        try {
                                          await declinePendingCheckin({
                                            memberId: p.memberId,
                                            entertainmentId,
                                          });
                                          setParticipantsTick(t => t + 1);
                                          setAssignmentsTick(t => t + 1);
                                        } catch (e) {
                                          toast.error('Fehler beim Ablehnen');
                                        }
                                      }}
                                    >
                                      Ablehnen
                                    </Button>
                                  </Stack>
                                </Stack>
                              ))}
                            </Stack>
                          </Paper>
                        )}
                      </Stack>
                    );
                  })()}
                </Box>
              )}
            <Box sx={{ userSelect: 'none' }}>
              <DndContext
                sensors={sensors}
                collisionDetection={pointerWithin}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
                onDragOver={handleDragOver}
              >
                {/* Always show upcoming/unassigned bookings, independent of check-ins */}
                {(() => {
                  // Build combined booking list without filtering by slot to allow mismatch warnings
                  const combined = [];
                  const addIfPresent = b => {
                    if (b) combined.push(b);
                  };
                  gaBookings.forEach(addIfPresent);
                  const deduped = [];
                  const seen = new Set();
                  for (const b of combined) {
                    const id = String(b?._id || b?.id || '');
                    if (!id || seen.has(id)) continue;
                    seen.add(id);
                    deduped.push(b);
                  }

                  const listForView =
                    selectedSlot === 'all' || effectiveSlotId == null
                      ? deduped
                      : deduped.filter(
                          b =>
                            b?.ltSpecific?.playerGroupID === effectiveSlotId ||
                            b?.ltSpecific?.playerGroupID == null
                        );

                  const unassigned = listForView.filter(
                    b => !groupAssignments.some(a => a.bookingId === b._id)
                  );

                  return (
                    <UnassignedDropZone globalOverId={overId}>
                      <Typography
                        variant='subtitle2'
                        color='text.secondary'
                        sx={{ mb: 1, pl: 0.5 }}
                      >
                        {unassigned.length > 0
                          ? 'Nicht zugewiesen'
                          : 'Hierher ziehen um Zuweisung aufzuheben'}
                      </Typography>
                      {unassigned.length > 0 && (
                        <SortableContext
                          items={unassigned.map(b => b._id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <Stack spacing={2} sx={{ mb: 2 }}>
                            {unassigned.map(booking => {
                              const totalCount = getAdjustedEffectiveParticipantCount(booking);
                              const canSplit =
                                !hasAnySplit && unassigned.length === 1 && totalCount > 1;
                              return (
                                <SortableBooking
                                  key={booking._id}
                                  id={booking._id}
                                  booking={booking}
                                  participants={
                                    participantsByBookingForCurrentPreparing.get(
                                      String(booking._id)
                                    ) || []
                                  }
                                  slotId={effectiveSlotId}
                                  canSplit={canSplit}
                                  onSplit={
                                    canSplit
                                      ? b => {
                                          if (hasAnySplit) return;
                                          if (teamCount > 2) {
                                            setSplitDialog({
                                              open: true,
                                              booking: b,
                                              teamA: 1,
                                              teamB: 2,
                                            });
                                          } else {
                                            handleSplitBooking(b, 1, 2);
                                          }
                                        }
                                      : null
                                  }
                                  onOpenDetails={handleOpenDetails}
                                  sitOutCount={sitOutCounts[String(booking._id)] || 0}
                                  onChangeSitOutCount={count =>
                                    handleChangeSitOutCount(String(booking._id), count)
                                  }
                                  isInformed={
                                    !!(currentPreparingGame?.informedByBooking || {})[
                                      String(booking._id)
                                    ]
                                  }
                                  onInformedChange={informed =>
                                    handleInformedChange(String(booking._id), informed)
                                  }
                                />
                              );
                            })}
                          </Stack>
                        </SortableContext>
                      )}
                    </UnassignedDropZone>
                  );
                })()}

                {/* Team assignment section */}
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 2,
                    mb: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box
                    sx={{
                      alignSelf: 'flex-end',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      minWidth: { xs: 0, sm: 220 },
                    }}
                  >
                    <FormControl
                      size='small'
                      sx={{
                        minWidth: 140,
                        gridColumn: { xs: 'span 1', sm: 'span 2' },
                      }}
                    >
                      <InputLabel>Anzahl Teams</InputLabel>
                      <Select
                        value={teamCount}
                        label='Anzahl Teams'
                        onChange={e => handleTeamCountChange(Number(e.target.value))}
                        sx={{
                          '& .MuiOutlinedInput-root': { height: 36 },
                          '& .MuiOutlinedInput-input': { py: 0.5 },
                        }}
                      >
                        {[2, 3, 4, 5, 6, 7].map(num => (
                          <MenuItem key={num} value={num}>
                            {num} Teams
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant='outlined'
                      color='primary'
                      onClick={async () => {
                        try {
                          if (!lastReuseTemplate) return;
                          const template = lastReuseTemplate;
                          const bookingsForView = combinedDedupedForCurrentSlot;
                          const visibleSet = new Set(bookingsForView.map(b => String(b._id)));
                          const filtered = template.assignments.filter(a =>
                            visibleSet.has(String(a.bookingId))
                          );
                          const nextAssignments = filtered.map(a => ({
                            bookingId: String(a.bookingId),
                            teamNumber: Number(a.teamNumber),
                            teamCount: Number(template.teamCount),
                          }));
                          const {
                            assignments: compactedAssignments,
                            teamCount: newTeamCount,
                            splitQuantitiesMap: compactedSplitQuantities,
                          } = compactTeamAssignments(
                            nextAssignments,
                            Number(template.teamCount),
                            {}
                          );
                          // Reconstruct split quantities for bookings that appear in multiple teams
                          const deriveSplitQuantities = assignments => {
                            try {
                              // Map bookings -> teams they are assigned to
                              const byBooking = new Map();
                              for (const a of assignments) {
                                const bid = String(a.bookingId);
                                if (!byBooking.has(bid)) byBooking.set(bid, []);
                                byBooking.get(bid).push(Number(a.teamNumber));
                              }
                              // Compute current team totals using only non-split bookings
                              const teamTotals = new Map();
                              for (
                                let i = 1;
                                i <= Number(newTeamCount || template.teamCount || teamCount);
                                i++
                              ) {
                                teamTotals.set(i, 0);
                              }
                              const singleAssignments = assignments.filter(
                                a => byBooking.get(String(a.bookingId)).length === 1
                              );
                              for (const a of singleAssignments) {
                                const booking = gaBookings.find(
                                  b => String(b._id) === String(a.bookingId)
                                );
                                const size = booking
                                  ? getAdjustedEffectiveParticipantCount(booking)
                                  : 0;
                                teamTotals.set(
                                  Number(a.teamNumber),
                                  (teamTotals.get(Number(a.teamNumber)) || 0) + Number(size || 0)
                                );
                              }

                              const result = {};
                              // For each split booking, allocate participants to minimize imbalance
                              for (const [bid, teams] of byBooking.entries()) {
                                if (!Array.isArray(teams) || teams.length < 2) continue;
                                const booking = gaBookings.find(b => String(b._id) === String(bid));
                                if (!booking) continue;
                                let remaining = Math.max(
                                  0,
                                  Number(getAdjustedEffectiveParticipantCount(booking)) || 0
                                );
                                if (remaining <= 1) continue;
                                const k = teams.length;
                                const counts = new Array(k).fill(0);
                                // Ensure at least 1 per team if possible
                                const minEach = Math.min(1, remaining);
                                for (let i = 0; i < k && remaining > 0 && minEach === 1; i++) {
                                  counts[i] += 1;
                                  teamTotals.set(teams[i], (teamTotals.get(teams[i]) || 0) + 1);
                                  remaining -= 1;
                                }
                                // Greedily assign remaining to the team with current minimal total
                                while (remaining > 0) {
                                  let bestIdx = 0;
                                  let bestTotal = teamTotals.get(teams[0]) || 0;
                                  for (let i = 1; i < k; i++) {
                                    const tTotal = teamTotals.get(teams[i]) || 0;
                                    if (tTotal < bestTotal) {
                                      bestTotal = tTotal;
                                      bestIdx = i;
                                    }
                                  }
                                  counts[bestIdx] += 1;
                                  teamTotals.set(
                                    teams[bestIdx],
                                    (teamTotals.get(teams[bestIdx]) || 0) + 1
                                  );
                                  remaining -= 1;
                                }
                                result[bid] = {};
                                for (let i = 0; i < k; i++) {
                                  result[bid][teams[i]] = counts[i];
                                }
                              }
                              return result;
                            } catch (_) {
                              return {};
                            }
                          };
                          const reconstructedSplits = deriveSplitQuantities(compactedAssignments);
                          const previousAssignments = groupAssignments;
                          const previousSplits = splitQuantities;
                          // Merge reconstructed splits with existing split quantities
                          // Only update split quantities for bookings that are being reconstructed
                          const merged = { ...previousSplits };
                          // Remove split quantities for bookings that are no longer split
                          const bookingsInAssignments = new Set(
                            compactedAssignments.map(a => String(a.bookingId))
                          );
                          const assignmentCountByBooking = new Map();
                          for (const a of compactedAssignments) {
                            const bid = String(a.bookingId);
                            assignmentCountByBooking.set(
                              bid,
                              (assignmentCountByBooking.get(bid) || 0) + 1
                            );
                          }
                          // Remove entries for bookings that are no longer split
                          for (const bid of Object.keys(merged)) {
                            if (
                              !bookingsInAssignments.has(bid) ||
                              (assignmentCountByBooking.get(bid) || 0) <= 1
                            ) {
                              delete merged[bid];
                            }
                          }
                          // Add or update reconstructed splits
                          for (const [bid, teamSplits] of Object.entries(reconstructedSplits)) {
                            merged[bid] = { ...teamSplits };
                          }
                          // Fallback to compacted split quantities if no reconstructed splits
                          if (
                            Object.keys(reconstructedSplits).length === 0 &&
                            compactedSplitQuantities
                          ) {
                            for (const [bid, teamSplits] of Object.entries(
                              compactedSplitQuantities
                            )) {
                              merged[bid] = { ...teamSplits };
                            }
                          }
                          setTeamCount(newTeamCount);
                          setGroupAssignments(compactedAssignments);
                          setSplitQuantities(merged);
                          await persistAssignments(
                            previousAssignments,
                            compactedAssignments,
                            newTeamCount,
                            getSlotParamForRequests()
                          );
                          await persistSplitQuantities(merged, previousSplits, newTeamCount);
                          setTeamAssignmentFull({
                            teamCount: newTeamCount,
                            assignments: compactedAssignments.map(a => ({
                              bookingId: a.bookingId,
                              teamNumber: a.teamNumber,
                            })),
                            splitQuantities: splitStateToArray(merged),
                          });
                        } catch (err) {
                          toast.error('Fehler beim Übernehmen der letzten Zuweisung');
                          setAssignmentsTick(t => t + 1);
                        }
                      }}
                      size='small'
                      disabled={!lastReuseAvailable}
                      title={lastReuseAvailable ? undefined : 'Keine letzte Zuweisung verfügbar'}
                      sx={{
                        minWidth: { xs: 'auto', sm: '120px' },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        px: { xs: 1, sm: 2 },
                      }}
                    >
                      Letzte Zuweisung übernehmen
                    </Button>
                    <Button
                      variant='outlined'
                      color='error'
                      onClick={() => setClearConfirmDialog({ open: true })}
                      size='small'
                      sx={{
                        minWidth: { xs: 'auto', sm: '120px' },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        px: { xs: 1, sm: 2 },
                        visibility: groupAssignments.length > 0 ? 'visible' : 'hidden',
                      }}
                    >
                      Zuweisung aufheben
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      border: '1px solid',
                      borderColor: 'primary.main',
                      borderRadius: 1,
                      maxWidth: 800,
                      px: 1,
                      pb: 1,
                    }}
                  >
                    <Typography
                      variant='subtitle2'
                      sx={{
                        my: 0.5,
                        fontWeight: 400,
                        fontSize: '0.875rem',
                        letterSpacing: '0.03em',
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <AutoAwesome sx={{ fontSize: 16 }} />
                      Automatisch zuweisen
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: 'repeat(1, minmax(0, 1fr))',
                          md: 'repeat(4, minmax(0, 1fr))',
                        },
                        gap: 1,
                      }}
                    >
                      <Button
                        variant='outlined'
                        color='primary'
                        onClick={handleAutoAssign}
                        size='small'
                        sx={{
                          minWidth: { xs: 'auto', sm: '120px' },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          px: { xs: 1, sm: 2 },
                          gridColumn: { xs: 'span 1', sm: 'span 2' },
                        }}
                      >
                        {teamCount} Teams
                      </Button>

                      <Button
                        variant='outlined'
                        color='primary'
                        onClick={handleAutoAssignNoSplit}
                        size='small'
                        sx={{
                          minWidth: { xs: 'auto', sm: '120px' },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          px: { xs: 1, sm: 2 },
                          gridColumn: { xs: 'span 1', sm: 'span 2' },
                        }}
                      >
                        {teamCount} Teams (ohne Split)
                      </Button>

                      <Button
                        variant='outlined'
                        color='primary'
                        onClick={() => handleAutoAssignBestTeamCount()}
                        size='small'
                        sx={{
                          minWidth: { xs: 'auto', sm: '120px' },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          px: { xs: 1, sm: 2 },
                          gridColumn: { xs: 'span 1', sm: 'span 2' },
                        }}
                      >
                        Optimale Teams
                      </Button>

                      <Button
                        variant='outlined'
                        color='primary'
                        onClick={() => handleAutoAssignBestTeamCountNoSplit()}
                        size='small'
                        sx={{
                          minWidth: { xs: 'auto', sm: '120px' },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          px: { xs: 1, sm: 2 },
                          gridColumn: { xs: 'span 1', sm: 'span 2' },
                        }}
                      >
                        optimale Teams (ohne Split)
                      </Button>
                    </Box>
                  </Box>
                </Box>

                <Box
                  display='grid'
                  gridTemplateColumns={{
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: teamCount === 3 || teamCount === 6 ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
                  }}
                  gap={2}
                >
                  {teamColors.slice(0, teamCount).map(team => (
                    <TeamDropZone
                      key={team.number}
                      team={team}
                      bookings={combinedDedupedForCurrentSlot.filter(b =>
                        groupAssignments.find(
                          a => a.bookingId === b._id && a.teamNumber === team.number
                        )
                      )}
                      participantsByBooking={participantsByBookingForCurrentPreparing}
                      slotId={effectiveSlotId}
                      globalOverId={overId}
                    />
                  ))}
                </Box>

                <DragOverlay dropAnimation={null}>
                  {activeBooking ? (
                    <BookingCard booking={activeBooking} onOpenDetails={handleOpenDetails} />
                  ) : null}
                </DragOverlay>
              </DndContext>
            </Box>
          </>
        </CardContent>
      </Card>
    );
  };

  const handleStart = async (gameId, activeSitOutOverride = null) => {
    try {
      setIsStartingGame(true);
      await startGame({ gameId, entertainmentId });
      // Clear current assignments and toggle slot when green/orange is selected
      setGroupAssignments([]);
      setSplitQuantities({});
      // Freeze sit-outs for active game and clear preparing counts
      setActiveSitOutCounts(prev => ({ ...(activeSitOutOverride || sitOutCounts) }));
      setSitOutCounts({});
      if (selectedSlot === 'green') {
        setSelectedSlot('orange');
      } else if (selectedSlot === 'orange') {
        setSelectedSlot('green');
      }
      setAssignmentsTick(t => t + 1);
      setParticipantsTick(t => t + 1);
    } catch (e) {
      toast.error('Fehler beim Starten des Spiels');
    } finally {
      setIsStartingGame(false);
    }
  };

  const handleRequestStart = async gameId => {
    try {
      // Build per-team missing players list and sit-out deltas per booking
      const pb = participantsByBookingForCurrentPreparing;
      const teamNumbers = Array.from({ length: Number(teamCount) }, (_, i) => i + 1);
      const bookingsByTeam = new Map();
      for (const tn of teamNumbers) bookingsByTeam.set(tn, []);
      for (const a of groupAssignments) {
        const tn = Number(a.teamNumber);
        const booking = gaBookings.find(b => String(b._id) === String(a.bookingId));
        if (booking && bookingsByTeam.has(tn)) bookingsByTeam.get(tn).push(booking);
      }

      const getAllocatedCheckedInForTeam = (bookingId, splitMap, teamNumber, totalCheckedIn) => {
        try {
          if (!splitMap || typeof splitMap !== 'object') {
            return Math.max(0, Number(totalCheckedIn) || 0);
          }
          const entries = Object.entries(splitMap)
            .map(([k, v]) => ({ team: Number(k), capacity: Math.max(0, Number(v) || 0) }))
            .filter(e => !Number.isNaN(e.team) && e.capacity > 0)
            .sort((a, b) => a.team - b.team);
          const totalCap = entries.reduce((s, e) => s + e.capacity, 0);
          let remaining = Math.max(0, Number(totalCheckedIn) || 0);
          if (totalCap <= 0 || remaining <= 0) return 0;
          const allocations = entries.map(e => {
            const raw = (remaining * e.capacity) / totalCap;
            const base = Math.min(e.capacity, Math.floor(raw));
            return { ...e, base, frac: raw - Math.floor(raw) };
          });
          let allocatedSum = allocations.reduce((s, a) => s + a.base, 0);
          let leftover = Math.max(0, remaining - allocatedSum);
          while (leftover > 0) {
            let bestIdx = -1;
            let bestFrac = -1;
            for (let i = 0; i < allocations.length; i++) {
              const a = allocations[i];
              if (a.base < a.capacity && a.frac > bestFrac) {
                bestFrac = a.frac;
                bestIdx = i;
              }
            }
            if (bestIdx === -1) break;
            allocations[bestIdx].base += 1;
            allocations[bestIdx].frac = 0;
            leftover -= 1;
          }
          const target = allocations.find(a => a.team === Number(teamNumber));
          return target ? Math.max(0, Math.min(target.base, target.capacity)) : 0;
        } catch (_) {
          return Math.max(0, Number(totalCheckedIn) || 0);
        }
      };

      const details = [];
      const deltasByBooking = {};
      const consumedByBooking = new Map();

      for (const tn of teamNumbers) {
        const teamBookingsList = bookingsByTeam.get(tn) || [];
        const teamItems = [];
        for (const booking of teamBookingsList) {
          const bid = String(booking._id);
          const adjustedTotal = getAdjustedEffectiveParticipantCount(booking);
          const splitMap = splitQuantities[bid];
          const teamTotal =
            splitMap && splitMap[tn] != null
              ? Math.max(0, Number(splitMap[tn]) || 0)
              : adjustedTotal;
          const checkedInForBookingTotal = (pb.get(bid) || []).filter(
            pm => !isPlaceholderMember(pm)
          ).length;
          const availableCheckedIn = Math.min(checkedInForBookingTotal, adjustedTotal);
          const allocatedCheckedIn = getAllocatedCheckedInForTeam(
            bid,
            splitMap,
            tn,
            availableCheckedIn
          );
          const teamMissing = Math.max(0, Number(teamTotal) - Number(allocatedCheckedIn));
          if (teamMissing <= 0) continue;

          const allMembers = Array.isArray(booking?.ltSpecific?.participatingMembers)
            ? booking.ltSpecific.participatingMembers
            : [];
          const checkedSet = new Set(
            (pb.get(bid) || []).map(x => String(typeof x === 'string' ? x : x?._id))
          );
          const notChecked = allMembers
            .map(m => (typeof m === 'string' ? { _id: m } : m || {}))
            .filter(m => !isPlaceholderMember(m))
            .filter(m => !checkedSet.has(String(m._id)));
          const consumed = consumedByBooking.get(bid) || 0;
          const slice = notChecked.slice(consumed, consumed + teamMissing);
          consumedByBooking.set(bid, consumed + slice.length);
          if (slice.length > 0) {
            teamItems.push({
              bookingId: bid,
              bookingName: `${booking.firstName || ''} ${booking.lastName || ''}`.trim() || bid,
              players: slice.map(m => ({
                memberId: String(m._id || ''),
                displayName:
                  `${m.firstName || ''}${m.firstName && m.playerName ? ' / ' : ''}${
                    m.playerName || ''
                  }`.trim() || String(m._id || ''),
              })),
            });
          }
          deltasByBooking[bid] = (deltasByBooking[bid] || 0) + teamMissing;
        }
        if (teamItems.length > 0) {
          details.push({ teamNumber: tn, items: teamItems });
        }
      }

      const hasMissing = details.length > 0;
      if (!hasMissing) {
        await handleStart(gameId);
        return;
      }
      setStartConfirmDialog({ open: true, gameId, details, deltasByBooking });
    } catch (_) {
      await handleStart(gameId);
    }
  };

  const handleProceedStartWithSitOuts = async () => {
    try {
      const { gameId, deltasByBooking } = startConfirmDialog;
      if (!gameId) {
        setStartConfirmDialog({ open: false, gameId: null, details: [], deltasByBooking: {} });
        return;
      }
      const nextMap = { ...sitOutCounts };
      const updates = [];
      for (const [bid, delta] of Object.entries(deltasByBooking || {})) {
        const inc = Math.max(0, Number(delta) || 0);
        if (inc <= 0) continue;
        const next = Math.max(0, Number(nextMap[bid] || 0) + inc || 0);
        nextMap[bid] = next;
        updates.push(
          updateSitOutForPreparing({ bookingId: String(bid), count: next, entertainmentId })
        );
      }
      setSitOutCounts(nextMap);
      try {
        await Promise.all(updates);
      } catch (_) {}
      setStartConfirmDialog({ open: false, gameId: null, details: [], deltasByBooking: {} });
      await handleStart(gameId, nextMap);
    } catch (e) {
      toast.error('Fehler beim Vorbereiten des Spielstarts');
      setStartConfirmDialog({ open: false, gameId: null, details: [], deltasByBooking: {} });
    }
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <ButtonGroup
          sx={{
            '& .MuiButton-root': {
              border: '1px solid',
              borderColor: 'divider',
              '&:not(:first-of-type)': {
                borderLeft: 'none',
              },
              '&:first-of-type': {
                borderTopLeftRadius: '4px',
                borderBottomLeftRadius: '4px',
              },
              '&:last-of-type': {
                borderTopRightRadius: '4px',
                borderBottomRightRadius: '4px',
              },
              '&:hover': {
                borderColor: 'divider',
                '& + .MuiButton-contained': {
                  borderLeft: 'none !important',
                },
                '&:has(+ .MuiButton-contained)': {
                  borderRight: 'none !important',
                },
              },
              '&:not(:last-of-type)': {
                borderRight: 'none',
                '&:hover': {
                  borderRight: 'none !important',
                },
              },
              '&:nth-of-type(2)': {
                borderRight: 'none',
                '&:hover': {
                  borderRight: 'none !important',
                },
              },
            },
            '& .MuiButton-contained': {
              border: 'none !important',
              '& + .MuiButton-root': {
                borderLeft: 'none !important',
              },
              '&:has(+ .MuiButton-contained)': {
                borderRight: 'none !important',
              },
              '&:hover': {
                border: 'none !important',
                '& + .MuiButton-root': {
                  borderLeft: 'none !important',
                },
                '&:has(+ .MuiButton-contained)': {
                  borderRight: 'none !important',
                },
              },
            },
          }}
        >
          <Button
            variant={selectedSlot === 'green' ? 'contained' : 'outlined'}
            color='success'
            onClick={() => setSelectedSlot('green')}
          >
            Grün
          </Button>
          <Button
            variant={selectedSlot === 'orange' ? 'contained' : 'outlined'}
            color='warning'
            onClick={() => setSelectedSlot('orange')}
          >
            Orange
          </Button>
          <Button
            variant={selectedSlot === 'all' ? 'contained' : 'outlined'}
            color='primary'
            onClick={() => setSelectedSlot('all')}
          >
            Alle
          </Button>
        </ButtonGroup>
        {!hideBranchEntertainmentSelectors && (
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <FormControl
              sx={{ minWidth: { xs: '100%', sm: 200 } }}
              size='small'
              disabled={branchOptions.length === 0}
            >
              <InputLabel>Branch</InputLabel>
              <Select value={branch} label='Branch' onChange={handleBranchChange}>
                {branchOptions.map(br => (
                  <MenuItem key={br.id} value={br.id}>
                    {br.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              sx={{ minWidth: { xs: '100%', sm: 200 } }}
              size='small'
              disabled={entertainmentOptions.length === 0}
            >
              <InputLabel>Entertainment</InputLabel>
              <Select
                value={entertainmentId}
                label='Entertainment'
                onChange={handleEntertainmentChange}
              >
                {entertainmentOptions.map(ent => (
                  <MenuItem key={ent.id} value={ent.id}>
                    {ent.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>

      {/* Audio Controls */}
      {entertainmentId && (
        <Paper sx={{ p: 1.5, mb: 2, backgroundColor: 'background.paper' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Stack direction='row' spacing={0.5} alignItems='center' sx={{ flexShrink: 0 }}>
              <VideoLibrary color='primary' sx={{ fontSize: 20 }} />
              <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                Ansagen
              </Typography>
            </Stack>

            {/* Compact buttons in one line */}
            <Stack
              direction='row'
              spacing={1}
              alignItems='flex-start'
              sx={{ flex: 1, minWidth: 0 }}
            >
              {selectedSlot === 'green' && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: 70,
                    }}
                  >
                    <Button
                      variant='outlined'
                      color='success'
                      size='small'
                      disabled={audioStatus.status !== 'online'}
                      startIcon={
                        audioStatus.status === 'playing' ? <VolumeUp sx={{ fontSize: 16 }} /> : null
                      }
                      onClick={async () => {
                        const success = await sendCommand('start', 'game-start-gruen.mp3', 'audio');
                        if (success) {
                          toast.success('Aufruf Grün gestartet');
                        }
                      }}
                      sx={{
                        borderColor: 'success.main',
                        color: 'success.main',
                        minWidth: 60,
                        px: 1,
                        py: 0.5,
                        fontSize: '0.75rem',
                        '&:hover': {
                          borderColor: 'success.main',
                          backgroundColor: alpha(theme.palette.success.main, 0.063),
                        },
                      }}
                    >
                      {audioStatus.status === 'playing' ? '▶' : 'Spiel grün'}
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: 70,
                    }}
                  >
                    <Button
                      variant='outlined'
                      color='success'
                      size='small'
                      disabled={audioStatus.status !== 'online'}
                      startIcon={
                        audioStatus.status === 'playing' ? <VolumeUp sx={{ fontSize: 16 }} /> : null
                      }
                      onClick={async () => {
                        const success = await sendCommand('start', 'briefing-gruen.mp3', 'audio');
                        if (success) {
                          toast.success('Aufruf Briefing Grün gestartet');
                        }
                      }}
                      sx={{
                        borderColor: 'success.main',
                        color: 'success.main',
                        minWidth: 60,
                        px: 1,
                        py: 0.5,
                        fontSize: '0.75rem',
                        '&:hover': {
                          borderColor: 'success.main',
                          backgroundColor: alpha(theme.palette.success.main, 0.063),
                        },
                      }}
                    >
                      {audioStatus.status === 'playing' ? '▶' : 'Brief. grün'}
                    </Button>
                  </Box>
                </>
              )}
              {selectedSlot === 'orange' && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: 70,
                    }}
                  >
                    <Button
                      variant='outlined'
                      color='warning'
                      size='small'
                      disabled={audioStatus.status !== 'online'}
                      startIcon={
                        audioStatus.status === 'playing' ? <VolumeUp sx={{ fontSize: 16 }} /> : null
                      }
                      onClick={async () => {
                        const success = await sendCommand(
                          'start',
                          'game-start-orange.mp3',
                          'audio'
                        );
                        if (success) {
                          toast.success('Aufruf Orange gestartet');
                        }
                      }}
                      sx={{
                        borderColor: 'warning.main',
                        color: 'warning.main',
                        minWidth: 60,
                        px: 1,
                        py: 0.5,
                        fontSize: '0.75rem',
                        '&:hover': {
                          borderColor: 'warning.main',
                          backgroundColor: alpha(theme.palette.warning.main, 0.063),
                        },
                      }}
                    >
                      {audioStatus.status === 'playing' ? '▶' : 'Spiel orange'}
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: 70,
                    }}
                  >
                    <Button
                      variant='outlined'
                      color='warning'
                      size='small'
                      disabled={audioStatus.status !== 'online'}
                      startIcon={
                        audioStatus.status === 'playing' ? <VolumeUp sx={{ fontSize: 16 }} /> : null
                      }
                      onClick={async () => {
                        const success = await sendCommand('start', 'briefing-orange.mp3', 'audio');
                        if (success) {
                          toast.success('Aufruf Briefing Orange gestartet');
                        }
                      }}
                      sx={{
                        borderColor: 'warning.main',
                        color: 'warning.main',
                        minWidth: 60,
                        px: 1,
                        py: 0.5,
                        fontSize: '0.75rem',
                        '&:hover': {
                          borderColor: 'warning.main',
                          backgroundColor: alpha(theme.palette.warning.main, 0.063),
                        },
                      }}
                    >
                      {audioStatus.status === 'playing' ? '▶' : 'Brief. orange'}
                    </Button>
                  </Box>
                </>
              )}
              {selectedSlot === 'all' && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: 60,
                    }}
                  >
                    <Button
                      variant='outlined'
                      color='primary'
                      size='small'
                      disabled={audioStatus.status !== 'online'}
                      startIcon={
                        audioStatus.status === 'playing' ? <VolumeUp sx={{ fontSize: 16 }} /> : null
                      }
                      onClick={async () => {
                        const success = await sendCommand('start', 'game-start-3min.mp3', 'audio');
                        if (success) {
                          toast.success('Spielstart 3min gestartet');
                        }
                      }}
                      sx={{
                        minWidth: 50,
                        px: 1,
                        py: 0.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      {audioStatus.status === 'playing' ? '▶' : '3min'}
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: 60,
                    }}
                  >
                    <Button
                      variant='outlined'
                      color='primary'
                      size='small'
                      disabled={audioStatus.status !== 'online'}
                      startIcon={
                        audioStatus.status === 'playing' ? <VolumeUp sx={{ fontSize: 16 }} /> : null
                      }
                      onClick={async () => {
                        const success = await sendCommand('start', 'game-start-now.mp3', 'audio');
                        if (success) {
                          toast.success('Spielstart Jetzt gestartet');
                        }
                      }}
                      sx={{
                        minWidth: 50,
                        px: 1,
                        py: 0.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      {audioStatus.status === 'playing' ? '▶' : 'JETZT'}
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: 60,
                    }}
                  >
                    <Button
                      variant='outlined'
                      color='primary'
                      size='small'
                      disabled={audioStatus.status !== 'online'}
                      startIcon={
                        audioStatus.status === 'playing' ? <VolumeUp sx={{ fontSize: 16 }} /> : null
                      }
                      onClick={async () => {
                        const success = await sendCommand('start', 'briefing-now.mp3', 'audio');
                        if (success) {
                          toast.success('Briefing Alle gestartet');
                        }
                      }}
                      sx={{
                        minWidth: 50,
                        px: 1,
                        py: 0.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      {audioStatus.status === 'playing' ? '▶' : 'Brief. alle'}
                    </Button>
                  </Box>
                </>
              )}
            </Stack>

            {/* Auto switches and timers in same line */}
            <Stack direction='row' spacing={1} alignItems='center' sx={{ flexShrink: 0 }}>
              <Tooltip
                title={
                  ['running', 'last30'].includes(briefingStatus.status)
                    ? 'Nicht verfügbar während das Briefing läuft'
                    : pendingWebview
                      ? 'Warte auf Bestätigung via MQTT...'
                      : 'Teameinteilung auf dem Briefing-Bildschirm anzeigen'
                }
              >
                <span>
                  <FormControlLabel
                    control={
                      <Switch
                        size='small'
                        checked={briefingStatus.status === 'webview'}
                        disabled={
                          ['running', 'last30'].includes(briefingStatus.status) || pendingWebview
                        }
                        onChange={async () => {
                          const isWebview = briefingStatus.status === 'webview';
                          pendingWebviewRef.current = true;
                          setPendingWebview(true);
                          const success = await sendCommand(
                            'webview',
                            isWebview ? 'hide' : 'show',
                            'briefing'
                          );
                          if (!success) {
                            pendingWebviewRef.current = false;
                            setPendingWebview(false);
                          }
                          if (success) {
                            toast.success(
                              isWebview
                                ? 'Teameinteilung ausgeblendet'
                                : 'Teameinteilung wird angezeigt'
                            );
                          }
                        }}
                      />
                    }
                    label={
                      <Stack direction='row' spacing={0.5} alignItems='center'>
                        <Tv sx={{ fontSize: 14 }} />
                        <Typography variant='caption'>Teameinteilung</Typography>
                      </Stack>
                    }
                    sx={{ m: 0 }}
                  />
                </span>
              </Tooltip>
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={autoAnnouncementsConfig?.briefing?.enabled || false}
                    onChange={async e => {
                      const enabled = e.target.checked;
                      const mode =
                        selectedSlot === 'all'
                          ? 'all'
                          : selectedSlot === 'green' || selectedSlot === 'orange'
                            ? 'slotted'
                            : autoAnnouncementsConfig?.mode || 'all';
                      const briefingDefault =
                        mode === 'all'
                          ? autoAnnouncementsConfig?.briefing?.defaultSecondsBefore || 300
                          : autoAnnouncementsConfig?.briefing?.defaultSecondsBefore || 180;
                      try {
                        await updateAutoAnnouncements({
                          mode,
                          briefing: {
                            enabled,
                            totalSecondsBefore: enabled
                              ? briefingDefault
                              : autoAnnouncementsConfig?.briefing?.totalSecondsBefore || 180,
                          },
                          gameStart: autoAnnouncementsConfig?.gameStart || {
                            enabled: false,
                            totalSecondsBefore: 60,
                          },
                        });
                      } catch (err) {
                        // Error already handled in hook
                      }
                    }}
                  />
                }
                label={<Typography variant='caption'>Auto Briefing</Typography>}
                sx={{ m: 0 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={autoAnnouncementsConfig?.gameStart?.enabled || false}
                    onChange={async e => {
                      const enabled = e.target.checked;
                      const mode =
                        selectedSlot === 'all'
                          ? 'all'
                          : selectedSlot === 'green' || selectedSlot === 'orange'
                            ? 'slotted'
                            : autoAnnouncementsConfig?.mode || 'all';
                      const gameStartDefault =
                        mode === 'all'
                          ? autoAnnouncementsConfig?.gameStart?.defaultSecondsBefore || 180
                          : autoAnnouncementsConfig?.gameStart?.defaultSecondsBefore || 60;
                      try {
                        await updateAutoAnnouncements({
                          mode,
                          briefing: autoAnnouncementsConfig?.briefing || {
                            enabled: false,
                            totalSecondsBefore: 180,
                          },
                          gameStart: {
                            enabled,
                            totalSecondsBefore: enabled
                              ? gameStartDefault
                              : autoAnnouncementsConfig?.gameStart?.totalSecondsBefore || 60,
                          },
                        });
                      } catch (err) {
                        // Error already handled in hook
                      }
                    }}
                  />
                }
                label={<Typography variant='caption'>Auto Start</Typography>}
                sx={{ m: 0 }}
              />
              {briefingCountdown !== null && (
                <Chip
                  label={formatCountdown(briefingCountdown)}
                  size='small'
                  color='primary'
                  variant='outlined'
                  onDelete={async () => {
                    const gameId = activeGame?._id ?? lastEndedGame?._id;
                    if (gameId) {
                      try {
                        await skipAutoAnnouncement('briefing', String(gameId));
                        setBriefingCountdown(null);
                      } catch (err) {
                        // Error handled in hook
                      }
                    }
                  }}
                  deleteIcon={
                    <Tooltip title='Überspringen'>
                      <IconButton size='small' sx={{ color: 'inherit', fontSize: '0.875rem' }}>
                        <Close sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  }
                  sx={{ height: 24, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
                />
              )}
              {gameStartCountdown !== null && (
                <Chip
                  label={formatCountdown(gameStartCountdown)}
                  size='small'
                  color='primary'
                  variant='outlined'
                  onDelete={async () => {
                    const gameId = activeGame?._id ?? lastEndedGame?._id;
                    if (gameId) {
                      try {
                        await skipAutoAnnouncement('gameStart', String(gameId));
                        setGameStartCountdown(null);
                      } catch (err) {
                        // Error handled in hook
                      }
                    }
                  }}
                  deleteIcon={
                    <Tooltip title='Überspringen'>
                      <IconButton size='small' sx={{ color: 'inherit', fontSize: '0.875rem' }}>
                        <Close sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  }
                  sx={{ height: 24, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
                />
              )}
            </Stack>

            <IconButton
              size='small'
              onClick={() => setAudioControlsExpanded(!audioControlsExpanded)}
              aria-expanded={audioControlsExpanded}
              aria-label='Status anzeigen'
              sx={{ flexShrink: 0 }}
            >
              {audioControlsExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={audioControlsExpanded}>
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              {/* Status Information */}
              <Stack direction='row' spacing={2} flexWrap='wrap' sx={{ mb: 2 }}>
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Audio: {audioStatus.displayStatus}
                  </Typography>
                  {audioStatus.receivedAt && (
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                      {dayjs(audioStatus.receivedAt).format('HH:mm:ss')}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Briefing: {briefingStatus.displayStatus}
                  </Typography>
                  {briefingStatus.receivedAt && (
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                      {dayjs(briefingStatus.receivedAt).format('HH:mm:ss')}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Laserforce: {lfStatus?.displayStatus || 'Laserforce ist OFFLINE'}
                  </Typography>
                  {lfStatus?.receivedAt && (
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                      {dayjs(lfStatus.receivedAt).format('HH:mm:ss')}
                    </Typography>
                  )}
                </Box>
              </Stack>

              {isLfLocked && (
                <Alert
                  severity='info'
                  sx={{
                    mb: 2,
                    py: 0.75,
                    px: 1.5,
                    textTransform: 'none',
                  }}
                >
                  <Typography variant='body2'>
                    Laserforce steuert aktuell das Spiel (
                    {lfStatus?.displayStatus || 'Status unbekannt'}
                    ). Start- und Stopp-Buttons im Operator sind vorübergehend gesperrt.
                  </Typography>
                </Alert>
              )}

              {/* Briefing Controls */}
              <Stack direction='row' spacing={1} flexWrap='wrap' alignItems='center' sx={{ mb: 2 }}>
                <Button
                  variant='contained'
                  color='primary'
                  size='small'
                  disabled={
                    !['looping', 'waitingForEmployee', 'webview'].includes(briefingStatus.status)
                  }
                  startIcon={
                    briefingStatus.status === 'running' ? (
                      <PlayCircle sx={{ fontSize: 16 }} />
                    ) : null
                  }
                  onClick={async () => {
                    const success = await sendCommand('start', 'de', 'briefing');
                    if (success) {
                      toast.success('Briefing auf Deutsch gestartet');
                    }
                  }}
                  sx={{ fontSize: '0.75rem', px: 1.5, py: 0.5 }}
                >
                  {briefingStatus.status === 'running' ? 'Läuft' : 'DE'}
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  size='small'
                  disabled={
                    !['looping', 'waitingForEmployee', 'webview'].includes(briefingStatus.status)
                  }
                  startIcon={
                    briefingStatus.status === 'running' ? (
                      <PlayCircle sx={{ fontSize: 16 }} />
                    ) : null
                  }
                  onClick={async () => {
                    const success = await sendCommand('start', 'en', 'briefing');
                    if (success) {
                      toast.success('Briefing mit Untertiteln gestartet');
                    }
                  }}
                  sx={{ fontSize: '0.75rem', px: 1.5, py: 0.5 }}
                >
                  {briefingStatus.status === 'running' ? 'Läuft' : 'EN'}
                </Button>
                {['running', 'last30', 'waitingForEmployee'].includes(briefingStatus.status) && (
                  <Tooltip title='Video stoppen'>
                    <IconButton
                      color='error'
                      size='small'
                      onClick={async () => {
                        const success = await sendCommand('stop', 'stop', 'briefing');
                        if (success) {
                          toast.success('Briefing gestoppt');
                        }
                      }}
                    >
                      <StopCircle sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              {/* Auto-Announcements Configuration */}
              {(autoAnnouncementsConfig?.briefing?.enabled ||
                autoAnnouncementsConfig?.gameStart?.enabled) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant='subtitle2' sx={{ mb: 2, fontWeight: 600 }}>
                    Automatische Ansagen Konfiguration
                  </Typography>

                  {/* Mode Selection */}
                  <FormControl component='fieldset' sx={{ mb: 2 }}>
                    <RadioGroup
                      row
                      value={autoAnnouncementsConfig?.mode || 'all'}
                      onChange={async e => {
                        const newMode = e.target.value;
                        try {
                          // Just send the mode change - backend will set defaults automatically
                          await updateAutoAnnouncements({
                            mode: newMode,
                          });
                        } catch (err) {
                          // Error handled in hook
                        }
                      }}
                    >
                      <FormControlLabel value='slotted' control={<Radio />} label='Slots' />
                      <FormControlLabel value='all' control={<Radio />} label='Alle' />
                    </RadioGroup>
                  </FormControl>

                  {/* Briefing Slider */}
                  {autoAnnouncementsConfig?.briefing?.enabled && (
                    <Box sx={{ mb: 3, px: 1 }}>
                      <Typography variant='body2' sx={{ mb: 1.5, fontWeight: 500 }}>
                        Briefing Zeitpunkt
                      </Typography>
                      <Slider
                        value={
                          briefingSliderValue !== null
                            ? briefingSliderValue
                            : autoAnnouncementsConfig.briefing.totalSecondsBefore || 180
                        }
                        onChange={(e, newValue) => {
                          setBriefingSliderValue(newValue);
                        }}
                        onChangeCommitted={async (e, newValue) => {
                          try {
                            await updateAutoAnnouncements({
                              mode: autoAnnouncementsConfig.mode,
                              briefing: {
                                ...autoAnnouncementsConfig.briefing,
                                totalSecondsBefore: newValue,
                              },
                              gameStart: autoAnnouncementsConfig.gameStart,
                            });
                            setBriefingSliderValue(null);
                          } catch (err) {
                            setBriefingSliderValue(null);
                            // Error handled in hook
                          }
                        }}
                        min={autoAnnouncementsConfig.mode === 'all' ? -300 : 0}
                        max={autoAnnouncementsConfig.mode === 'all' ? 300 : 540}
                        step={15}
                        marks={[
                          ...(autoAnnouncementsConfig.mode === 'all'
                            ? [{ value: -300, label: '-5min' }]
                            : []),
                          { value: 0, label: '0min' },
                          { value: 60, label: '1min' },
                          { value: 180, label: '3min' },
                          { value: 300, label: '5min' },
                          ...(autoAnnouncementsConfig.mode === 'slotted'
                            ? [{ value: 540, label: '9min' }]
                            : []),
                        ]}
                        valueLabelDisplay='on'
                        valueLabelFormat={value => {
                          if (value < 0) {
                            return `${Math.abs(value / 60)}min nach`;
                          }
                          return `${value / 60}min vor`;
                        }}
                        sx={{
                          '& .MuiSlider-thumb': {
                            width: 20,
                            height: 20,
                            '&:hover': {
                              boxShadow: `0 0 0 8px ${alpha(theme.palette.primary.main, 0.16)}`,
                            },
                            '&.Mui-active': {
                              boxShadow: `0 0 0 12px ${alpha(theme.palette.primary.main, 0.16)}`,
                            },
                          },
                          '& .MuiSlider-track': {
                            height: 4,
                          },
                          '& .MuiSlider-rail': {
                            height: 4,
                          },
                          touchAction: 'pan-x',
                        }}
                      />
                    </Box>
                  )}

                  {/* Game Start Slider */}
                  {autoAnnouncementsConfig?.gameStart?.enabled && (
                    <Box sx={{ mb: 2, px: 1 }}>
                      <Typography variant='body2' sx={{ mb: 1.5, fontWeight: 500 }}>
                        Spielstart Zeitpunkt
                      </Typography>
                      <Slider
                        value={
                          gameStartSliderValue !== null
                            ? gameStartSliderValue
                            : autoAnnouncementsConfig.gameStart.totalSecondsBefore || 60
                        }
                        onChange={(e, newValue) => {
                          setGameStartSliderValue(newValue);
                        }}
                        onChangeCommitted={async (e, newValue) => {
                          try {
                            await updateAutoAnnouncements({
                              mode: autoAnnouncementsConfig.mode,
                              briefing: autoAnnouncementsConfig.briefing,
                              gameStart: {
                                ...autoAnnouncementsConfig.gameStart,
                                totalSecondsBefore: newValue,
                              },
                            });
                            setGameStartSliderValue(null);
                          } catch (err) {
                            setGameStartSliderValue(null);
                            // Error handled in hook
                          }
                        }}
                        min={autoAnnouncementsConfig.mode === 'all' ? -600 : 0}
                        max={autoAnnouncementsConfig.mode === 'all' ? 180 : 300}
                        step={15}
                        marks={[
                          ...(autoAnnouncementsConfig.mode === 'all'
                            ? [{ value: -600, label: '-10min' }]
                            : []),
                          { value: 0, label: '0min' },
                          { value: 60, label: '1min' },
                          { value: 180, label: '3min' },
                          ...(autoAnnouncementsConfig.mode === 'slotted'
                            ? [{ value: 300, label: '5min' }]
                            : []),
                        ]}
                        valueLabelDisplay='on'
                        valueLabelFormat={value => {
                          if (value < 0) {
                            return `${Math.abs(value / 60)}min nach`;
                          }
                          return `${value / 60}min vor`;
                        }}
                        sx={{
                          '& .MuiSlider-thumb': {
                            width: 20,
                            height: 20,
                            '&:hover': {
                              boxShadow: `0 0 0 8px ${alpha(theme.palette.primary.main, 0.16)}`,
                            },
                            '&.Mui-active': {
                              boxShadow: `0 0 0 12px ${alpha(theme.palette.primary.main, 0.16)}`,
                            },
                          },
                          '& .MuiSlider-track': {
                            height: 4,
                          },
                          '& .MuiSlider-rail': {
                            height: 4,
                          },
                          touchAction: 'pan-x',
                        }}
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Collapse>
        </Paper>
      )}

      <Grid container spacing={2}>
        {activeGame && (
          <Grid item xs={12}>
            {renderActiveGameCard(activeGame)}
          </Grid>
        )}
        {/* CountUp display for last ended game */}
        {!activeGame && countUpData && (
          <Grid item xs={12}>
            <Paper
              sx={{
                marginBottom: 1.5,
                padding: 1.5,
                flexShrink: 0,
                backgroundColor: countUpData.isRed ? theme.palette.error.main : countUpData.color,
                color: theme.palette.getContrastText(
                  countUpData.isRed ? theme.palette.error.main : countUpData.color || theme.palette.primary.main
                ),
              }}
              elevation={3}
            >
              <Stack direction='row' spacing={2} alignItems='center' justifyContent='center'>
                <Typography
                  variant='body2'
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    whiteSpace: 'nowrap',
                    opacity: 0.9,
                    textAlign: 'center',
                  }}
                >
                  {countUpData.slotLabel} – seit Spielende:
                </Typography>
                <Stack direction='row' spacing={1.5} alignItems='center' justifyContent='center'>
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.5rem',
                      lineHeight: 1,
                      minWidth: '80px',
                      textAlign: 'center',
                      fontFamily: 'monospace',
                    }}
                  >
                    {String(countUpData.endedMinutesAgo).padStart(2, '0')}:
                    {String(countUpData.endedSeconds).padStart(2, '0')}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        )}
        <Grid item xs={12}>
          {renderNextPreparingCard(currentPreparingGame, currentPreparingSlotId)}
        </Grid>
      </Grid>

      {/* Dialog to choose two teams for splitting when more than 2 teams exist */}
      <Dialog
        open={splitDialog.open}
        onClose={() => setSplitDialog({ open: false, booking: null, teamA: 1, teamB: 2 })}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle>Aufteilen auf Teams</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
            <FormControl size='small' fullWidth>
              <InputLabel>Team A</InputLabel>
              <Select
                label='Team A'
                value={splitDialog.teamA}
                onChange={e => setSplitDialog(prev => ({ ...prev, teamA: Number(e.target.value) }))}
              >
                {Array.from({ length: teamCount }, (_, i) => i + 1).map(n => (
                  <MenuItem key={`A-${n}`} value={n}>
                    Team {teamColors[n - 1]?.name || n}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size='small' fullWidth>
              <InputLabel>Team B</InputLabel>
              <Select
                label='Team B'
                value={splitDialog.teamB}
                onChange={e => setSplitDialog(prev => ({ ...prev, teamB: Number(e.target.value) }))}
              >
                {Array.from({ length: teamCount }, (_, i) => i + 1).map(n => (
                  <MenuItem key={`B-${n}`} value={n}>
                    Team {teamColors[n - 1]?.name || n}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
            Bitte zwei unterschiedliche Teams auswählen.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSplitDialog({ open: false, booking: null, teamA: 1, teamB: 2 })}
          >
            Abbrechen
          </Button>
          <Button
            onClick={() => {
              const { booking, teamA, teamB } = splitDialog;
              if (!booking)
                return setSplitDialog({ open: false, booking: null, teamA: 1, teamB: 2 });
              if (teamA === teamB) {
                toast.error('Bitte zwei verschiedene Teams wählen');
                return;
              }
              if (hasAnySplit) {
                toast.error('Es kann immer nur eine Buchung aufgeteilt werden');
                return;
              }
              handleSplitBooking(booking, teamA, teamB);
              setSplitDialog({ open: false, booking: null, teamA: 1, teamB: 2 });
            }}
            variant='contained'
          >
            Aufteilen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog to confirm clearing all assignments */}
      <Dialog
        open={clearConfirmDialog.open}
        onClose={() => setClearConfirmDialog({ open: false })}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleClearAssignments();
            setClearConfirmDialog({ open: false });
          }
        }}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle>Zuweisungen aufheben</DialogTitle>
        <DialogContent>
          <Typography variant='body1'>
            Möchtest du wirklich alle Teamzuweisungen aufheben? Diese Aktion kann nicht rückgängig
            gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearConfirmDialog({ open: false })}>Abbrechen</Button>
          <Button
            onClick={() => {
              handleClearAssignments();
              setClearConfirmDialog({ open: false });
            }}
            variant='contained'
            color='error'
          >
            Aufheben
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog to confirm game end */}
      <Dialog
        open={endConfirmDialog.open}
        onClose={() => setEndConfirmDialog({ open: false, game: null })}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleEndGame();
          }
        }}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle>Spiel beenden</DialogTitle>
        <DialogContent>
          <Typography variant='body1'>Möchtest du wirklich das laufende Spiel beenden?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndConfirmDialog({ open: false, game: null })}>
            Abbrechen
          </Button>
          <Button onClick={handleEndGame} variant='contained' color='error'>
            Spiel beenden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog to confirm start with missing check-ins */}
      <Dialog
        open={startConfirmDialog.open}
        onClose={() =>
          setStartConfirmDialog({ open: false, gameId: null, details: [], deltasByBooking: {} })
        }
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Nicht alle Spieler eingecheckt</DialogTitle>
        <DialogContent>
          <Typography variant='body1' sx={{ mb: 1 }}>
            In folgenden Teams sind noch Spieler nicht eingecheckt:
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {startConfirmDialog.details.map(section => (
              <Paper
                key={`team-${section.teamNumber}`}
                sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
                  Team {teamColors[section.teamNumber - 1]?.name || section.teamNumber}
                </Typography>
                <Stack spacing={1}>
                  {section.items.map(item => (
                    <Box key={`${section.teamNumber}-${item.bookingId}`}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        {item.bookingName}
                      </Typography>
                      <List dense sx={{ ml: 2 }}>
                        {item.players.map(p => (
                          <ListItem key={p.memberId} sx={{ py: 0.25 }}>
                            <Typography variant='body2'>{p.displayName}</Typography>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            ))}
          </Stack>
          <Typography variant='caption' color='text.secondary' sx={{ mt: 1.5, display: 'block' }}>
            Diese Spieler werden automatisch als aussetzend markiert, wenn du fortfährst.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setStartConfirmDialog({ open: false, gameId: null, details: [], deltasByBooking: {} })
            }
            color='error'
          >
            Abbrechen
          </Button>
          <Button onClick={handleProceedStartWithSitOuts} variant='contained'>
            Spiel trotzdem starten
          </Button>
        </DialogActions>
      </Dialog>

      {/* Late check-in dialog */}
      <Dialog
        open={lateCheckinDialog.open}
        onClose={() => setLateCheckinDialog({ open: false, list: [] })}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle>Verspäteter Westen-Check-in</DialogTitle>
        <DialogContent>
          <Typography variant='body1' sx={{ mb: 1 }}>
            Verspätete Westen-Check-Ins sind nur innerhalb von 5 Minuten möglich.
          </Typography>
          <Typography variant='body2'>
            Bitte beende das laufende Spiel, um Check-ins für das nächste Spiel zu erlauben.
          </Typography>
          {Array.isArray(lateCheckinDialog.list) && lateCheckinDialog.list.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
                Ausstehende verspätete Check-ins
              </Typography>
              <List dense>
                {lateCheckinDialog.list.map(p => (
                  <ListItem key={p.memberId} sx={{ py: 0.5 }}>
                    <Typography variant='body2'>
                      {(p.firstName || '') +
                        (p.firstName && p.playerName ? ' / ' : '') +
                        (p.playerName || p.memberId)}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLateCheckinDialog({ open: false, list: [] })}>OK</Button>
          <Button
            onClick={() => setEndConfirmDialog({ open: true, game: activeGame })}
            color='error'
          >
            Spiel beenden
          </Button>
          <Button
            onClick={async () => {
              try {
                const list = Array.isArray(lateCheckinDialog.list) ? lateCheckinDialog.list : [];
                await Promise.all(
                  list.map(x => acceptLateCheckin({ memberId: x.memberId, entertainmentId }))
                );
                // Decrease active sit-out counts for affected bookings
                try {
                  setActiveSitOutCounts(prev => {
                    const next = { ...prev };
                    for (const x of list) {
                      const booking = memberIdToBooking.get(String(x.memberId));
                      const bid = booking ? String(booking._id) : null;
                      if (!bid) continue;
                      const current = Math.max(0, Number(next[bid] || 0));
                      if (current > 0) next[bid] = current - 1;
                    }
                    return next;
                  });
                } catch (_) {}
                setLateCheckinDialog({ open: false, list: [] });
                setParticipantsTick(t => t + 1);
                setAssignmentsTick(t => t + 1);
              } catch (e) {
                toast.error('Fehler beim Hinzufügen');
              }
            }}
          >
            Trotzdem hinzufügen
          </Button>
        </DialogActions>
      </Dialog>
      {/* Booking details modal */}
      <BookingDetailsModal
        open={detailsModal.open}
        onClose={handleCloseDetails}
        bookingId={detailsModal.bookingId}
        borderColor={detailsModal.borderColor}
        onUpdate={handleDetailsUpdate}
      />
    </Box>
  );
};

export default Operator;

// TODO split up this file into smaller components!
